import { FullPerson, Person } from '../../models/person';
import { Action, createSelector, Select, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  AddEmergencyContact,
  AddEmployeeDeduction,
  AddEmployeeEarning,
  AddEmployeeStatus,
  AddJobCode,
  AddLaborAllocation,
  BuildLocalTaxForm,
  ClearAlertModal,
  ClearEmergencyContacts,
  ClearEmployeeStatuses,
  ClearEmploymentTypeCollections,
  ClearPerson,
  ClearSSN,
  EditEmergencyContact,
  EditEmployeeStatus,
  InitFormData,
  LoadJobDetailsForm,
  LoadPerson,
  LoadPersonalDetailsForm,
  LoadUniversalCompanyConfigs,
  LoadUniversalEnums,
  LoadUniversalPersonLists,
  MarkActiveFormAsTouched,
  MarkFormsPristine,
  PersonStatusChanged,
  RefreshEmployeeStatuses,
  RefreshPerson,
  RefreshPersonalDetailsLists,
  RefreshPersonLists,
  ReloadDirectDeposits,
  ReloadEmployeeDeductions,
  ReloadEmployeeEarnings,
  RemoveEmergencyContact,
  RemoveLaborAllocation,
  ResetAddressFields,
  ResetEditorForms,
  ResetEditorState,
  ResetJobDetailsForm,
  ResetPersonalDetailsForm,
  ResetTaxesForm,
  SavePerson,
  SetAllowDirectDeposit,
  SetCalendarId,
  SetCurrentTabDirty,
  SetCurrentTabStatus,
  SetDirectDepositFormDirty,
  SetDirectDeposits,
  SetEditorMode,
  SetEmergencyContacts,
  SetEmployeeDeductions,
  SetEmployeeEarnings,
  SetEmployeeStateTaxExemptionsByState,
  SetFederalIdChanged,
  SetHeader,
  SetJobDetails,
  SetLaborAllocations,
  SetMaskFederalId,
  SetOccupationChanged,
  SetPayFrequencyDetails,
  SetPersonalDetails,
  SetPersonHasFederalId,
  SetReadOnly,
  SetSaveButtonText,
  SetSaveInProgress,
  SetShowVerifyFederalId,
  SetTabHasChanged,
  SetTaxes,
  SetTaxExemptions,
  SetUseNewHireReporting,
  TriggerSave,
  UpdateEmployeeDeduction,
  UpdateEmployeeEarning,
  UpdateLaborAllocation
} from './PersonEditor.actions';
import { PeopleService } from '../../services/people.service';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { JobDetailsForm, PersonalDetailsForm, TaxesForm } from './FormTypes';
import { append, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import { formatDatePickerValue } from '../../utils/datepicker-value-parser';
import { Enum, KeyPair } from '../../models/base';
import { EnumService } from '../../services/enum.service';
import { forkJoin, of } from 'rxjs';
import { Client } from '../../models/client';
import { CompanyEarningConfig } from '../../models/company-earning-config';
import { CompaniesService } from '../../services/companies.service';
import { EmployeeStatus } from '../../models/person-status';
import { CompanyLocationConfig } from '../../models/company-location-config';
import { Navigate } from '@ngxs/router-plugin';
import { EmergencyContact } from '../../models/emergency-contact';
import { EmployeeLaborAllocation } from '../../models/labor-allocation';
import { CompanyLocalTaxConfig, CompanyTaxCodeConfig } from '../../models/tax-config';
import { WorkersCompCode } from '../../workers-comp';
import { ModalMessage, ModalStates } from '../../models/modal';
import { ApiResponse } from '../../models/api-response';
import { FlattenResponseMessages, GetResponseModalStyle } from '../../utils/api-response-helper';
import { getObjectDeltas } from '../../utils/object-deltas';
import {
  EmployeeTax,
  EmployeeTaxes,
  EmployeeTaxExemptions,
  StateTaxExemption,
  StateTaxExemptions
} from '../../models/employee-tax';
import { PersonValidationService } from '../../services/person-validation.service';
import { PersonSaveService } from '../../services/person-save.service';
import { ModalService } from '../../services/modal.service';
import { EmployeeDeduction } from '../../models/employee-deductions';
import { EmployeeEarning } from '../../models/employee-earning';
import { EmployeeDirectDepositExtended } from '../../models/employee-direct-deposit';
import { EmployeePTO, PTOConfig } from '../../models/paid-time-off';
import { CompanyDeductionConfig } from '../../models/company-deduction-config';
import { ToastService } from '../../services/toast.service';
import { AuditService } from '../../services/audit.service';
import { EmploymentTypes } from '../../models/employment-types';
import { ResetForm, SetFormPristine } from '@ngxs/form-plugin';
import * as _ from 'underscore';
import { ClientSelectionState } from '../ClientSelectionState/ClientSelection.state';
import {
  SetBasisOfPayment,
  SetDepartment,
  SetDivision,
  SetEmployeeId,
  SetFullTimePartTime,
  SetHireDate,
  SetIncludeNonW2InWorkersComp,
  SetIsOwnerOfficer,
  SetIsSeasonal,
  SetJobDetailsFormState,
  SetJobTitle,
  SetLaborAllocationTotal,
  SetLocation,
  SetOccupationId,
  SetOKToAutoPay,
  SetOriginalHireDate,
  SetPayFrequency,
  SetShouldReportAsNewHire,
  SetSOCCode,
  SetWorkEmail,
  SetWorkersCompCode,
  SetWorkersCompLimitId,
  SetWorkersCompOwnerStatus,
  SetWorkPhoneNumber,
  SetWorksIn
} from './PersonEditor.JobDetailsForm.actions';
import {
  SetBirthday,
  SetCity,
  SetCountry,
  SetEmploymentTypeId,
  SetFederalIdentifier,
  SetFirstName,
  SetGender,
  SetHomePhoneNumber,
  SetIsCompany,
  SetIsNonUSAddress,
  SetLastName,
  SetMaritalStatus,
  SetMiddleName,
  SetMobilePhoneNumber,
  SetPersonalDetailsFormState,
  SetPersonalEmailAddress,
  SetPostalCode,
  SetRace,
  SetState,
  SetStreetAddress,
  SetStreetAddress2,
  SetUseEIN,
  SetVerifyFederalIdentifier
} from './PersonEditor.PersonalDetailsForm.actions';
import {
  SetAddlStateAddlAllowances,
  SetAddlStateAddlWithholdingType,
  SetAddlStateAddlWithholdingValue,
  SetAddlStateFilingStatus,
  SetAddlStateWithholdingAllowance,
  SetChildTaxCredit,
  SetExpireDate,
  SetFederalAddlWithholdingType,
  SetFederalAddlWithholdingValue,
  SetFederalFilingStatus,
  SetFederalWithholdingAllowance,
  SetFUTAExemptionReason,
  SetHasDisLeaveExempt,
  SetHasFamilyLeaveExempt,
  SetHasFUTAExemption,
  SetHasMEDCExemption,
  SetHasMedLeaveExempt,
  SetHasSSExemption,
  SetHasSUIExemption,
  SetIsExemptFromWithholding,
  SetIsHigherWithholding,
  SetJ1F1Visa,
  SetLocals,
  SetOtherIncome,
  SetStateAddlAllowances,
  SetStateAddlWithholdingType,
  SetStateAddlWithholdingValue,
  SetStateFilingStatus,
  SetStateWithholdingAllowance,
  SetTaxesFormState,
  SetW4Version,
  SetWageDeduction,
  SetWithHoldingState
} from './PersonEditor.TaxesForm.actions';
import { HexToText, TextToHex } from '../../utils/stringUtils';

export interface IPersonEditorState {
  Person: Person;
  Forms: {
    PersonalDetailsForm: {
      model: PersonalDetailsForm;
      dirty: boolean;
      status: string;
      errors: any;
    };
    JobDetailsForm: {
      model: JobDetailsForm;
      dirty: boolean;
      status: string;
      errors: any;
    };
    TaxesForm: {
      model: TaxesForm;
      dirty: boolean;
      status: string;
      errors: any;
    };
  };
  IsCurrentTabValid: boolean;
  IsCurrentTabDirty: boolean;
  EditorMode: string;
  MaskFederalId: boolean;
  FederalIdChanged: boolean;
  PersonHasFederalId: boolean;
  Client: Client;
  UseNewHireReporting: boolean;
  AllowDirectDeposits: boolean;
  DirectDepositFormDirty: boolean;
  HasChangedTabs: boolean;
  ReadOnly: boolean;
  TaxExemptions: EmployeeTaxExemptions;
  StateTaxExemptions: string[];
  OccupationChanged: boolean;
  ClientStateTaxExemptions: string[];
  ShowVerifyFederalId: boolean;
  SaveButtonText: string;
  SaveInProgress: boolean;
  AlertModalMessage: ModalMessage | null;
  ModalStyle: ModalStates;
  //We need a way to trigger the forms to mark themselves touched in certain cases
  //Since the forms are now self-contained and I can't trigger 'touched' state from here
  //this property exists so I can make a selector do that.
  TriggerFormsTouched: boolean;
  TriggerFormsPristine: boolean;
  Header: {
    Name: string;
    EmployeeType: string;
    Status: string;
  };
  Enums: {
    States: Enum[];
    EmployeeStatuses: Enum[];
    DeductionTypes: Enum[];
  };
  PersonLists: {
    Statuses: EmployeeStatus[];
    EmergencyContacts: EmergencyContact[];
    LaborAllocations: EmployeeLaborAllocation[];
    Deductions: EmployeeDeduction[];
    Earnings: EmployeeEarning[];
    EmployeePTOs: EmployeePTO[];
    Taxes: EmployeeTaxes;
    DirectDeposits: EmployeeDirectDepositExtended[];
  };
  CompanyConfigs: {
    Locations: CompanyLocationConfig[];
    Earnings: CompanyEarningConfig[];
    TaxConfigs: CompanyTaxCodeConfig[];
    JobCodes: KeyPair[];
    WorkersCompCodes: WorkersCompCode[];
    Deductions: CompanyDeductionConfig[];
    LocalTaxs: CompanyLocalTaxConfig[];
    PTOConfigs: PTOConfig[];
  };
}
@State<IPersonEditorState>({
  name: 'personEditor',
  defaults: {
    Person: new Person(),
    Forms: {
      PersonalDetailsForm: {
        model: {
          EmploymentTypeId: null,
          UseEIN: null,
          FederalIdentifier: null,
          VerifyFederalIdentifier: null,
          IsCompany: false,
          FirstName: null,
          MiddleName: null,
          LastName: null,
          Birthday: null,
          PersonalEmailAddress: null,
          HomePhoneNumber: null,
          MobilePhoneNumber: null,
          Gender: null,
          MaritalStatus: null,
          Race: null,
          StreetAddress: null,
          StreetAddress2: null,
          City: null,
          State: null,
          PostalCode: null,
          Country: null,
          IsNonUSAddress: false
        },
        dirty: false,
        errors: null,
        status: null
      },
      JobDetailsForm: {
        model: {
          EmployeeId: null,
          HireDate: null,
          ShouldReportAsNewHire: null,
          WorksIn: null,
          OKToAutoPay: '0',
          PayFrequency: null,
          Location: null,
          Division: null,
          Department: null,
          JobTitle: null,
          IsSeasonal: null,
          FullTimePartTime: 'F',
          IsOwnerOfficer: null,
          SOCCode: null,
          OccupationId: null,
          BasisOfPayment: null,
          WorkEmailAddress: null,
          WorkPhoneNumber: null,
          OriginalHireDate: null,
          IncludeNonW2InWorkersComp: null,
          WorkersCompCode: null,
          WorkersCompOwnerStatus: null,
          WorkersCompLimitId: null,
          LaborAllocationTotal: null
        },
        dirty: false,
        errors: null,
        status: null
      },
      TaxesForm: {
        model: {
          FederalFilingStatus: null,
          FederalWithholdingAllowance: null,
          FederalAddlWithholdingType: null,
          FederalAddlWithholdingValue: null,
          HasSSExemption: null,
          HasMEDCExemption: null,
          HasSUIExemption: null,
          HasFUTAExemption: null,
          HasDisLeaveExempt: null,
          HasFamilyLeaveExempt: null,
          HasMedLeaveExempt: null,
          FUTAExemptionReason: null,
          J1F1Visa: null,
          ExpireDate: null,
          WithHoldingState: null,
          StateFilingStatus: null,
          StateWithholdingAllowance: null,
          StateAddlAllowances: null,
          StateAddlWithholdingType: null,
          StateAddlWithholdingValue: null,
          AddlStateFilingStatus: null,
          AddlStateWithholdingAllowance: null,
          AddlStateAddlAllowances: null,
          AddlStateAddlWithholdingType: null,
          AddlStateAddlWithholdingValue: null,
          IsExemptFromWithholding: null,
          W4Version: null,
          IsHigherWithholding: null,
          ChildTaxCredit: null,
          OtherIncome: null,
          WageDeduction: null,
          Locals: []
        },
        dirty: false,
        errors: null,
        status: null
      }
    },
    AlertModalMessage: null,
    MaskFederalId: false,
    FederalIdChanged: false,
    PersonHasFederalId: false,
    Client: null,
    HasChangedTabs: false,
    IsCurrentTabValid: false,
    IsCurrentTabDirty: false,
    EditorMode: 'add',
    UseNewHireReporting: false,
    AllowDirectDeposits: false,
    ModalStyle: ModalStates.Warning,
    TriggerFormsTouched: false,
    TriggerFormsPristine: false,
    ShowVerifyFederalId: false,
    DirectDepositFormDirty: false,
    SaveInProgress: false,
    ReadOnly: false,
    TaxExemptions: new EmployeeTaxExemptions(),
    OccupationChanged: false,
    SaveButtonText: 'Continue',
    StateTaxExemptions: ['HasDisLeaveExempt', 'HasFamilyLeaveExempt', 'HasMedLeaveExempt'],
    ClientStateTaxExemptions: ['HasClientDisLeave', 'HasClientFamilyLeave', 'HasClientMedLeave'],
    Header: {
      Name: '',
      EmployeeType: '',
      Status: ''
    },
    Enums: {
      States: [],
      EmployeeStatuses: [],
      DeductionTypes: []
    },
    PersonLists: {
      Statuses: [],
      EmergencyContacts: [],
      LaborAllocations: [],
      Deductions: [],
      Earnings: [],
      EmployeePTOs: [],
      Taxes: new EmployeeTaxes(),
      DirectDeposits: []
    },
    CompanyConfigs: {
      Locations: [],
      Earnings: [],
      TaxConfigs: [],
      JobCodes: [],
      WorkersCompCodes: [],
      Deductions: [],
      LocalTaxs: [],
      PTOConfigs: []
    }
  }
})
@Injectable()
export class PersonEditorState {
  constructor(
    private peopleService: PeopleService,
    private enumService: EnumService,
    private companyService: CompaniesService,
    private store: Store,
    private validationService: PersonValidationService,
    private saveService: PersonSaveService,
    private modalService: ModalService,
    private toastService: ToastService,
    private auditService: AuditService
  ) {}

  //This action handler is up here so that it's closer to where the defaults are assigned
  // in the decorator, since they would need to be changed in tandem.
  @Action(ResetEditorState)
  resetEditorState(ctx: StateContext<IPersonEditorState>) {
    ctx.setState({
      Person: new Person(),
      Forms: {
        PersonalDetailsForm: {
          model: {
            EmploymentTypeId: null,
            UseEIN: null,
            FederalIdentifier: null,
            VerifyFederalIdentifier: null,
            IsCompany: false,
            FirstName: null,
            MiddleName: null,
            LastName: null,
            Birthday: null,
            PersonalEmailAddress: null,
            HomePhoneNumber: null,
            MobilePhoneNumber: null,
            Gender: null,
            MaritalStatus: null,
            Race: null,
            StreetAddress: null,
            StreetAddress2: null,
            City: null,
            State: null,
            PostalCode: null,
            Country: null,
            IsNonUSAddress: null
          },
          dirty: false,
          errors: null,
          status: null
        },
        JobDetailsForm: {
          model: {
            EmployeeId: null,
            HireDate: null,
            ShouldReportAsNewHire: null,
            WorksIn: null,
            OKToAutoPay: '0',
            PayFrequency: null,
            Location: null,
            Division: null,
            Department: null,
            JobTitle: null,
            IsSeasonal: null,
            FullTimePartTime: 'F',
            IsOwnerOfficer: null,
            SOCCode: null,
            OccupationId: null,
            BasisOfPayment: null,
            WorkEmailAddress: null,
            WorkPhoneNumber: null,
            OriginalHireDate: null,
            IncludeNonW2InWorkersComp: null,
            WorkersCompCode: null,
            WorkersCompOwnerStatus: null,
            WorkersCompLimitId: null,
            LaborAllocationTotal: null
          },
          dirty: false,
          errors: null,
          status: null
        },
        TaxesForm: {
          model: {
            FederalFilingStatus: null,
            FederalWithholdingAllowance: null,
            FederalAddlWithholdingType: null,
            FederalAddlWithholdingValue: null,
            HasSSExemption: null,
            HasMEDCExemption: null,
            HasSUIExemption: null,
            HasFUTAExemption: null,
            HasDisLeaveExempt: null,
            HasFamilyLeaveExempt: null,
            HasMedLeaveExempt: null,
            FUTAExemptionReason: null,
            J1F1Visa: null,
            ExpireDate: null,
            WithHoldingState: null,
            StateFilingStatus: null,
            StateWithholdingAllowance: null,
            StateAddlAllowances: null,
            StateAddlWithholdingType: null,
            StateAddlWithholdingValue: null,
            AddlStateFilingStatus: null,
            AddlStateWithholdingAllowance: null,
            AddlStateAddlAllowances: null,
            AddlStateAddlWithholdingType: null,
            AddlStateAddlWithholdingValue: null,
            IsExemptFromWithholding: null,
            W4Version: null,
            IsHigherWithholding: null,
            ChildTaxCredit: null,
            OtherIncome: null,
            WageDeduction: null,
            Locals: []
          },
          dirty: false,
          errors: null,
          status: null
        }
      },
      MaskFederalId: false,
      FederalIdChanged: false,
      PersonHasFederalId: false,
      Client: null,
      HasChangedTabs: false,
      AlertModalMessage: null,
      ModalStyle: ModalStates.Warning,
      IsCurrentTabValid: false,
      IsCurrentTabDirty: false,
      EditorMode: 'add',
      UseNewHireReporting: false,
      AllowDirectDeposits: false,
      TriggerFormsTouched: false,
      TriggerFormsPristine: false,
      ShowVerifyFederalId: false,
      DirectDepositFormDirty: false,
      SaveInProgress: false,
      ReadOnly: false,
      TaxExemptions: new EmployeeTaxExemptions(),
      OccupationChanged: false,
      SaveButtonText: 'Continue',
      StateTaxExemptions: ['HasDisLeaveExempt', 'HasFamilyLeaveExempt', 'HasMedLeaveExempt'],
      ClientStateTaxExemptions: ['HasClientDisLeave', 'HasClientFamilyLeave', 'HasClientMedLeave'],
      Header: {
        Name: '',
        EmployeeType: '',
        Status: ''
      },
      Enums: {
        States: [],
        EmployeeStatuses: [],
        DeductionTypes: []
      },
      PersonLists: {
        Statuses: [],
        EmergencyContacts: [],
        LaborAllocations: [],
        Deductions: [],
        Earnings: [],
        EmployeePTOs: [],
        Taxes: new EmployeeTaxes(),
        DirectDeposits: []
      },
      CompanyConfigs: {
        Locations: [],
        Earnings: [],
        TaxConfigs: [],
        JobCodes: [],
        WorkersCompCodes: [],
        Deductions: [],
        LocalTaxs: [],
        PTOConfigs: []
      }
    });
  }

  //Selectors
  @Selector()
  static hasFederalIdentifier(state: IPersonEditorState) {
    return state.PersonHasFederalId;
  }

  @Selector()
  static getStates(state: IPersonEditorState) {
    return state.Enums.States;
  }

  @Selector()
  static getAlertModalMessage(state: IPersonEditorState) {
    return {
      msg: state.AlertModalMessage,
      style: state.ModalStyle
    };
  }

  @Selector()
  static getPerson(state: IPersonEditorState) {
    return state.Person;
  }

  @Selector()
  static getEarningConfigs(state: IPersonEditorState) {
    return state.CompanyConfigs.Earnings;
  }

  @Selector()
  static getFederalLastFour(state: IPersonEditorState) {
    if (state.Forms.PersonalDetailsForm.model.FederalIdentifier) {
      return state.Forms.PersonalDetailsForm.model.FederalIdentifier.substring(5);
    } else if (state.Person && state.Person.FederalIdentifier) {
      return state.Person.FederalIdentifier.substring(5);
    }
    return '';
  }

  @Selector()
  static getHeaderName(state: IPersonEditorState) {
    return state.Header.Name;
  }

  @Selector()
  static getHeaderEmployeeType(state: IPersonEditorState) {
    return state.Header.EmployeeType;
  }

  @Selector()
  static getHeaderStatus(state: IPersonEditorState) {
    return state.Header.Status;
  }

  @Selector()
  static getHasChangedTabs(state: IPersonEditorState) {
    return state.HasChangedTabs;
  }

  @Selector()
  static getFederalIdChanged(state: IPersonEditorState) {
    return state.FederalIdChanged;
  }

  @Selector()
  static getMaskFederalId(state: IPersonEditorState) {
    return state.MaskFederalId;
  }

  @Selector()
  static getPersonHasFederalId(state: IPersonEditorState) {
    return state.PersonHasFederalId;
  }

  @Selector()
  static getEmployeeStatuses(state: IPersonEditorState) {
    return state.PersonLists.Statuses;
  }

  @Selector()
  static getPotentialEmployeeStatuses(state: IPersonEditorState) {
    return state.Enums.EmployeeStatuses;
  }

  @Selector()
  static getReadOnly(state: IPersonEditorState) {
    return state.ReadOnly;
  }

  @Selector()
  static getSavePersonInfo(state: IPersonEditorState) {
    return {
      Statuses: state.PersonLists.Statuses,
      EmergencyContacts: state.PersonLists.EmergencyContacts,
      LaborAllocations: state.PersonLists.LaborAllocations
    };
  }

  @Selector()
  static getSaveButtonText(state: IPersonEditorState) {
    return state.SaveButtonText;
  }

  @Selector()
  static getEmergencyContacts(state: IPersonEditorState) {
    return state.PersonLists.EmergencyContacts;
  }

  @Selector()
  static getCompanyTaxConfigs(state: IPersonEditorState) {
    return state.CompanyConfigs.TaxConfigs;
  }

  @Selector()
  static getJobCodes(state: IPersonEditorState) {
    return state.CompanyConfigs.JobCodes;
  }

  @Selector()
  static getLaborAllocations(state: IPersonEditorState) {
    return state.PersonLists.LaborAllocations;
  }

  @Selector()
  static getLocations(state: IPersonEditorState) {
    return state.CompanyConfigs.Locations;
  }

  @Selector()
  static getUseNewHireReporting(state: IPersonEditorState) {
    return state.UseNewHireReporting;
  }

  @Selector()
  static getWorkersCompCodes(state: IPersonEditorState) {
    return state.CompanyConfigs.WorkersCompCodes;
  }

  @Selector()
  static getEditorLeaveValues(state: IPersonEditorState) {
    return {
      TaxCodes: state.CompanyConfigs.TaxConfigs,
      WorkersCompCodes: state.CompanyConfigs.WorkersCompCodes
    };
  }

  @Selector()
  static getState(state: IPersonEditorState) {
    return state;
  }

  @Selector()
  static getEmploymentTypeId(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.EmploymentTypeId;
  }

  @Selector()
  static getDeductionConfigs(state: IPersonEditorState) {
    return state.CompanyConfigs.Deductions;
  }

  @Selector()
  static getEmployeeDeductions(state: IPersonEditorState) {
    return state.PersonLists.Deductions;
  }

  @Selector()
  static getDisplayedDeductions(state: IPersonEditorState) {
    // see US 5069 for more details
    const deductions: EmployeeDeduction[] = [];
    state.PersonLists.Deductions.forEach(d => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === d.Id);

      // bug 7945. non-rate deductions should be displayed
      // if any property has changed from the default state
      if (
        config &&
        config.IsActive &&
        ((d.Rates && d.Rates.length > 0) || !this._isDeductionInDefaultState(d))
      ) {
        deductions.push(d);
      }
    });

    return deductions;
  }

  @Selector()
  static getAvailableDeductions(state: IPersonEditorState) {
    const deductions: CompanyDeductionConfig[] = [];
    const displayed = this.getDisplayedDeductions(state);
    const employmentType = state.Forms.PersonalDetailsForm.model.EmploymentTypeId;
    state.CompanyConfigs.Deductions.forEach(c => {
      // only show those not yet in the table
      if (c.IsActive && !displayed.find(x => x.Id === c.Id)) {
        // see US 5067 for more info about employment type matching
        switch (employmentType) {
          case EmploymentTypes.W2: {
            if (
              c.EmploymentTypeId !== EmploymentTypes.M1099 &&
              c.EmploymentTypeId !== EmploymentTypes.K1
            ) {
              deductions.push(c);
            }
            break;
          }
          case EmploymentTypes.M1099: {
            if (
              c.EmploymentTypeId !== EmploymentTypes.W2 &&
              c.EmploymentTypeId !== EmploymentTypes.K1
            ) {
              deductions.push(c);
            }
            break;
          }
          case EmploymentTypes.K1: {
            if (
              c.EmploymentTypeId !== EmploymentTypes.W2 &&
              c.EmploymentTypeId !== EmploymentTypes.M1099
            ) {
              deductions.push(c);
            }
            break;
          }
          default: {
            break;
          }
        }
      }
    });
    return deductions;
  }

  static getBenefitType(id: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      if (id) {
        const config = state.CompanyConfigs.Deductions.find(x => x.Id == id);
        if (config) {
          const type = state.Enums.DeductionTypes.find(t => t.Id == config.Type.toString());
          return type ? type.Value : '';
        }
      }
      return '';
    });
  }

  static getDeductionAmountPrefix(deductionId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === deductionId);
      return config && !config.IsPercentageDeduction ? '$' : '';
    });
  }

  static getDeductionAmountSuffix(deductionId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === deductionId);
      return config && config.IsPercentageDeduction ? '%' : '';
    });
  }

  static getDeductionConfig(deductionId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      return state.CompanyConfigs.Deductions.find(x => x.Id === deductionId);
    });
  }

  static getDeductionName(deductionId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === deductionId);
      return config ? config.Name : null;
    });
  }

  static getThirdPartyPayees(deductionId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === deductionId);
      return config ? config.ThirdPartyPayees : [];
    });
  }

  static canDeductionHaveRates(deductionId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === deductionId);
      return config && config.CanHaveRates;
    });
  }

  static canDeductionHaveAnnualLimit(deductionId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === deductionId);
      return config && config.CanHaveAnnualLimit;
    });
  }

  static canDeductionEditDisbursements(id: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === id);
      return config && config.DisbursementAtEmployee;
    });
  }

  static canDeductionOfferMedIns(id: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === id);
      return config && config.MasterCode === 'CSUPP';
    });
  }

  static canDeductionShowDisbursements(id: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === id);
      return config && config.ShowDisbursementControls;
    });
  }

  static canDeductionHaveDisposableLimits(id: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Deductions.find(x => x.Id === id);
      return config && config.RequiresDisposableLimit;
    });
  }

  @Selector()
  static getEmployeeEarnings(state: IPersonEditorState) {
    return state.PersonLists.Earnings;
  }

  @Selector()
  static getAvailableEarnings(state: IPersonEditorState) {
    const earnings: CompanyEarningConfig[] = [];

    const displayed = this.getDisplayedEarnings(state);
    state.CompanyConfigs.Earnings.forEach(c => {
      const employmentType = this.getEmploymentTypeId(state);
      if (
        (c.CanHaveRates || c.CanSetHours) &&
        c.IsActive &&
        c.EmploymentTypeId == employmentType &&
        !displayed.find(x => x.Id === c.Id)
      ) {
        earnings.push(c);
      }
    });

    return earnings;
  }

  @Selector()
  static getDisplayedEarnings(state: IPersonEditorState) {
    // see US 5068 for more details
    const earnings: EmployeeEarning[] = [];

    state.PersonLists.Earnings.forEach(earning => {
      let config = state.CompanyConfigs.Earnings.find(x => x.Id === earning.Id);
      if (config == null) {
        console.warn(
          `Earning config not found for earning id: ${earning.Id}. Attempting to trim ids.`
        );
        //Might be extra whitespace. Try again with trimmed id.
        const idConverted = HexToText(earning.Id);
        const trimmedId = TextToHex(idConverted.trim());
        config = state.CompanyConfigs.Earnings.find(
          x => TextToHex(HexToText(x.Id).trim()) === trimmedId
        );
        if (config == null) {
          console.error(`Earning config not found for earning id: ${earning.Id}.`);
          return;
        }
      }

      if (
        (earning.Rates && earning.Rates.length > 0 && config.CanHaveRates) || // user defined rate
        parseFloat(earning.HoursPaidPerPayroll.toString()) > 0 || // user defined auto pay hours
        !earning.IsActive
      ) {
        /* user clicked diable flag*/ earnings.push(earning);
      }
    });

    return earnings;
  }

  static canEarningHaveRates(earningId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      if (earningId) {
        const config = state.CompanyConfigs.Earnings.find(x => x.Id === earningId);
        return config.CanHaveRates || false;
      }
      return null;
    });
  }

  static canEarningSetHours(earningId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      if (earningId) {
        const config = state.CompanyConfigs.Earnings.find(x => x.Id === earningId);
        return config.CanSetHours || false;
      }
      return null;
    });
  }

  static getEarningAmountPrefix(earningId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Earnings.find(x => x.Id === earningId);
      return config && !config.IsPercentageEarning ? '$' : '';
    });
  }

  static getEarningAmountSuffix(earningId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const config = state.CompanyConfigs.Earnings.find(x => x.Id === earningId);
      return config && config.IsPercentageEarning ? '%' : '';
    });
  }

  static getEarningCalc(earningId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const c = state.CompanyConfigs.Earnings.find(x => x.Id === earningId);
      return c && c.CalculationMethod ? c.CalculationMethod : null;
    });
  }

  static getEarningName(earningId: string) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      const c = state.CompanyConfigs.Earnings.find(x => x.Id === earningId);
      if (c && c.Name) {
        return c.Name;
      }
      return null;
    });
  }

  @Selector()
  static getPayFrequency(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.PayFrequency;
  }

  @Selector()
  static getEmployeeTaxes(state: IPersonEditorState) {
    return state.PersonLists.Taxes;
  }

  @Selector()
  static getEmployeeTaxExemptions(state: IPersonEditorState) {
    return state.TaxExemptions;
  }

  @Selector()
  static isExemptFromWithholding(state: IPersonEditorState) {
    const configs = state.CompanyConfigs.TaxConfigs;
    const taxes = state.PersonLists.Taxes.FedAndState;
    const fedTaxCode = configs.find(x => x.EntityType === 'F').TaxCode;
    const federal = taxes.find(x => x.TaxCode === fedTaxCode && x.IsActive);
    return federal.IsExemptFromWithholding;
  }

  @Selector()
  static getEmployeeState(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.State;
  }

  @Selector()
  static getEmployeeWorksIn(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.WorksIn;
  }

  @Selector()
  static isPersonRecordComplete(state: IPersonEditorState) {
    const taxesDisabled =
      state.Forms.PersonalDetailsForm.model.EmploymentTypeId == EmploymentTypes.M1099 ||
      state.Forms.PersonalDetailsForm.model.EmploymentTypeId == EmploymentTypes.K1;
    //NGXS Forms Module sets the status to "VALID" when the form is valid.
    //'null' means the tab has not been visited yet.
    // in Edit mode, this is as good as 'VALID' because the person record couldn't have been saved if it wasn't valid.
    if (
      (state.Forms.PersonalDetailsForm.status === 'VALID' ||
        (state.Forms.PersonalDetailsForm.status === null && state.EditorMode == 'edit')) &&
      (state.Forms.JobDetailsForm.status === 'VALID' ||
        (state.Forms.JobDetailsForm.status === null && state.EditorMode == 'edit')) &&
      // taxes form disabled for 1099
      (state.Forms.TaxesForm.status === 'VALID' ||
        taxesDisabled ||
        (state.Forms.TaxesForm.status === null && state.EditorMode == 'edit'))
    ) {
      return true;
    } else {
      return false;
    }
  }

  @Selector()
  static areFormsValid(state: IPersonEditorState) {
    return {
      PersonalDetailsForm: state.Forms.PersonalDetailsForm.status === 'VALID',
      JobDetailsForm: state.Forms.JobDetailsForm.status === 'VALID',
      TaxesForm: state.Forms.TaxesForm.status === 'VALID'
    };
  }

  @Selector()
  static getCurrentTabStatus(state: IPersonEditorState) {
    return {
      IsValid: state.IsCurrentTabValid,
      IsDirty: state.IsCurrentTabDirty
    };
  }

  @Selector()
  static markFormTouched(state: IPersonEditorState) {
    return state.TriggerFormsTouched;
  }

  @Selector()
  static areFormsPristine(state: IPersonEditorState) {
    return {
      PersonalDetailsForm: !state.Forms.PersonalDetailsForm.dirty,
      JobDetailsForm: !state.Forms.JobDetailsForm.dirty,
      TaxesForm: !state.Forms.TaxesForm.dirty,
      All:
        !state.Forms.PersonalDetailsForm.dirty &&
        !state.Forms.JobDetailsForm.dirty &&
        !state.Forms.TaxesForm.dirty
    };
  }

  @Selector()
  static getPTOConfigs(state: IPersonEditorState) {
    return state.CompanyConfigs.PTOConfigs;
  }

  @Selector()
  static getEditorMode(state: IPersonEditorState) {
    return state.EditorMode;
  }

  @Selector()
  static getEmployeePTOs(state: IPersonEditorState) {
    return state.PersonLists.EmployeePTOs;
  }

  static getPtoConfig(id: number) {
    return createSelector([PersonEditorState], (state: IPersonEditorState) => {
      return state.CompanyConfigs.PTOConfigs.find(x => x.Id === id);
    });
  }

  @Selector()
  static getEmployeeDirectDeposits(state: IPersonEditorState) {
    return state.PersonLists.DirectDeposits;
  }

  @Selector()
  static getDirectDepositFormDirty(state: IPersonEditorState) {
    return state.DirectDepositFormDirty;
  }

  @Selector()
  static getPersonalDetailsFormModel(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model;
  }

  @Selector()
  static getJobDetailsFormModel(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model;
  }

  @Selector()
  static getFormModels(state: IPersonEditorState) {
    return {
      PersonalDetails: state.Forms.PersonalDetailsForm.model,
      JobDetails: state.Forms.JobDetailsForm.model,
      Validity: PersonEditorState.areFormsValid(state)
    };
  }

  @Selector()
  static getAllowDirectDeposit(state: IPersonEditorState) {
    return state.AllowDirectDeposits;
  }

  @Selector()
  static getShowVerifyFederalId(state: IPersonEditorState) {
    return state.ShowVerifyFederalId;
  }

  @Selector()
  static isNonW2Employee(state: IPersonEditorState) {
    return (
      state.Forms.PersonalDetailsForm.model.EmploymentTypeId === EmploymentTypes.M1099 ||
      state.Forms.PersonalDetailsForm.model.EmploymentTypeId === EmploymentTypes.K1
    );
  }

  //ActionHandlers

  @Action(SetDirectDepositFormDirty)
  setDirectDepositFormDirty(
    ctx: StateContext<IPersonEditorState>,
    { dirty }: SetDirectDepositFormDirty
  ) {
    ctx.patchState({
      DirectDepositFormDirty: dirty
    });
  }

  @Action(SetSaveButtonText)
  setSaveButtonText(ctx: StateContext<IPersonEditorState>, { text }: SetSaveButtonText) {
    ctx.patchState({
      SaveButtonText: text
    });
  }

  @Action(SetDirectDeposits)
  setDirectDeposits(ctx: StateContext<IPersonEditorState>, { directDeposits }: SetDirectDeposits) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          DirectDeposits: directDeposits
        })
      })
    );
  }

  @Action(ClearPerson)
  clearPerson(ctx: StateContext<IPersonEditorState>) {
    ctx.patchState({
      Person: new Person()
    });
  }

  @Action(TriggerSave)
  triggerSave(
    ctx: StateContext<IPersonEditorState>,
    { clientCode, tabElemIds, activeTabId }: TriggerSave
  ) {
    const state = ctx.getState();
    const isComplete = PersonEditorState.isPersonRecordComplete(state);

    if (isComplete) {
      this.store.dispatch(new SavePerson(clientCode, tabElemIds, activeTabId)).subscribe(() => {
        this.store.dispatch(new InitFormData());
        this.store.dispatch(new SetSaveButtonText('Save & Continue'));
      });
    }
  }

  @Action(SetPersonalDetails)
  setPersonalDetails(ctx: StateContext<IPersonEditorState>) {
    let state = ctx.getState();
    const p = state.Person;

    if (!p.State || !p.State.trim()) {
      p.State = '';
    }

    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: {
              EmploymentTypeId: p.EmploymentTypeId,
              UseEIN: p.UseEIN ? '1' : '0',
              FederalIdentifier: '',
              VerifyFederalIdentifier: null,
              IsCompany: p.IsCompany,
              FirstName: p.FirstName,
              MiddleName: p.MiddleName,
              LastName: p.LastName,
              Birthday: formatDatePickerValue(p.Birthday),
              PersonalEmailAddress: p.PersonalEmailAddress,
              HomePhoneNumber: p.HomePhoneNumber ? p.HomePhoneNumber.trim() : '',
              MobilePhoneNumber: p.MobilePhoneNumber ? p.MobilePhoneNumber.trim() : '',
              Gender: p.Gender,
              MaritalStatus: p.MaritalStatus,
              Race: p.Race,
              StreetAddress: p.StreetAddress,
              StreetAddress2: p.StreetAddress2,
              City: p.City,
              State: p.State,
              PostalCode:
                p.PostalCode + (p.PostalCodeExtension ? p.PostalCodeExtension.trim() : ''),
              Country: p.Country,
              IsNonUSAddress: p.IsNonUSAddress
            }
          })
        }),
        PersonHasFederalId: !!p.FederalIdentifier
      })
    );
    return ctx.dispatch(new SetHeader());
  }

  @Action(SetJobDetails)
  setJobDetails(ctx: StateContext<IPersonEditorState>) {
    let state = ctx.getState();
    const p = state.Person;

    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: {
              EmployeeId: p.EmployeeId,
              HireDate: formatDatePickerValue(p.HireDate),
              ShouldReportAsNewHire: p.ShouldReportAsNewHire ? '1' : '0',
              WorksIn: p.WorksIn,
              OKToAutoPay: p.OKToAutoPay ? '1' : '0',
              PayFrequency: p.PayFrequency,
              Location: p.Location,
              Division: p.Division,
              Department: p.Department,
              JobTitle: p.JobTitle,
              IsSeasonal: p.IsSeasonal,
              FullTimePartTime: p.FullTimePartTime || 'F',
              IsOwnerOfficer: p.IsOwnerOfficer,
              SOCCode: p.SOCCode,
              OccupationId: p.OccupationId,
              BasisOfPayment: p.BasisOfPayment,
              WorkEmailAddress: p.WorkEmailAddress,
              WorkPhoneNumber: p.WorkPhoneNumber,
              OriginalHireDate: formatDatePickerValue(p.OriginalHireDate),
              IncludeNonW2InWorkersComp: p.IncludeNonW2InWorkersComp,
              WorkersCompCode: p.WorkersCompCode,
              WorkersCompOwnerStatus: p.WorkersCompOwnerStatus
                ? p.WorkersCompOwnerStatus.toString()
                : '1',
              WorkersCompLimitId: p.WorkersCompLimitId,
              LaborAllocationTotal: null
            }
          })
        })
      })
    );
  }

  @Action(RefreshPersonLists)
  refreshPersonLists(ctx: StateContext<IPersonEditorState>) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(clientCode => {
        const state = ctx.getState();
        const p = state.Person;
        if (p.Id) {
          return this.store.dispatch(new LoadUniversalPersonLists(clientCode, p.Id, true)).pipe(
            mergeMap(() => {
              return this.store.dispatch(new InitFormData());
            })
          );
        }
        return of();
      })
    );
  }

  @Action(SetShowVerifyFederalId)
  setShowVerifyFederalId(
    ctx: StateContext<IPersonEditorState>,
    { showVerifyFederalId }: SetShowVerifyFederalId
  ) {
    ctx.patchState({
      ShowVerifyFederalId: showVerifyFederalId
    });
  }

  @Action(InitFormData)
  initFormData(ctx: StateContext<IPersonEditorState>) {
    const obs = [
      ctx.dispatch(new SetPersonalDetails()),
      ctx.dispatch(new SetJobDetails()),
      ctx.dispatch(new SetTaxes()),
      ctx.dispatch(new SetTaxExemptions()),
      ctx.dispatch(new MarkFormsPristine())
    ];
    return forkJoin(obs);
  }

  @Action(ResetEditorForms)
  resetEditorForms(ctx: StateContext<IPersonEditorState>) {
    ctx.dispatch(new ResetForm({ path: 'personEditor.Forms.PersonalDetailsForm' }));
    ctx.dispatch(new ResetForm({ path: 'personEditor.Forms.JobDetailsForm' }));
    ctx.dispatch(new ResetForm({ path: 'personEditor.Forms.TaxesForm' }));
  }

  @Selector()
  static triggerMarkFormPristine(state: IPersonEditorState) {
    return state.TriggerFormsPristine;
  }

  @Action(MarkFormsPristine)
  markFormsPristine(ctx: StateContext<IPersonEditorState>) {
    ctx.patchState({
      TriggerFormsPristine: !ctx.getState().TriggerFormsPristine
    });
  }

  @Action(MarkActiveFormAsTouched)
  markActiveFormAsTouched(ctx: StateContext<IPersonEditorState>) {
    let state = ctx.getState();
    ctx.setState(
      patch<IPersonEditorState>({
        TriggerFormsTouched: !state.TriggerFormsTouched
      })
    );
  }

  @Action(SetTaxes)
  setTaxes(ctx: StateContext<IPersonEditorState>) {
    const state = ctx.getState();

    const taxCtrls = structuredClone(state.Forms.TaxesForm.model);
    const taxes = state.PersonLists.Taxes.FedAndState;
    const configs = state.CompanyConfigs.TaxConfigs;
    const person = state.Person;

    // find the active employee tax object
    // where associated company tax config's entity type is 'F' (federal)
    const fedTaxCode = configs.find(x => x.EntityType === 'F').TaxCode;
    const federal = taxes.find(x => x.TaxCode === fedTaxCode && x.IsActive);
    if (federal) {
      if (!federal.IsExemptFromWithholding) {
        taxCtrls.FederalFilingStatus = federal.FilingStatus;
        taxCtrls.FederalWithholdingAllowance = federal.WithholdingAllowance;
        taxCtrls.ChildTaxCredit = federal.ChildTaxCredit;
        taxCtrls.OtherIncome = federal.OtherIncome;
        taxCtrls.WageDeduction = federal.WageDeduction;
      }

      taxCtrls.FederalAddlWithholdingType = federal.AdditionalWithholdingType.trim() || 'null';
      taxCtrls.FederalAddlWithholdingValue = federal.AdditionalWithholdingAmount;

      taxCtrls.IsExemptFromWithholding = federal.IsExemptFromWithholding;
      taxCtrls.W4Version = federal.W4Version.toString();
      taxCtrls.IsHigherWithholding = federal.IsHigherWithholding;
    } else {
      taxCtrls.W4Version = '1';
      taxCtrls.FederalFilingStatus = 'S ';
      taxCtrls.FederalAddlWithholdingType = 'null';
    }

    // find any active employee state taxes
    let workStateTaxes: EmployeeTax = null;
    let homeStateTaxes: EmployeeTax = null;

    taxes.forEach(tax => {
      // lookup state income tax config
      const c = configs.find(x => x.TaxCode === tax.TaxCode);
      if (tax.IsActive && c && c.EntityType === 'S') {
        // we have an active state tax. check if it's work or home state
        if (c.StateAbbreviation === person.WorksIn) {
          workStateTaxes = tax;
        }

        if (
          person.State &&
          person.State !== person.WorksIn &&
          c.StateAbbreviation === person.State
        ) {
          homeStateTaxes = tax;
        }
      }
    });
    // if we have active work and home state taxes - select 'Both'
    if (workStateTaxes !== null && homeStateTaxes !== null) {
      taxCtrls.WithHoldingState = `Both ${workStateTaxes.StateAbbreviation} ${homeStateTaxes.StateAbbreviation}`;
    } else if (workStateTaxes !== null) {
      taxCtrls.WithHoldingState = person.WorksIn;
    } else if (homeStateTaxes !== null) {
      taxCtrls.WithHoldingState = person.State;
    } else {
      // there is a tax election mismatch
      const t = state.PersonLists.Taxes.FedAndState.find(x => {
        const config = configs.find(y => y.TaxCode == x.TaxCode);
        return x.IsActive && config?.EntityType === 'S';
      });
      taxCtrls.WithHoldingState = t ? t.StateAbbreviation : '';
    }

    if (workStateTaxes !== null) {
      taxCtrls.StateFilingStatus = workStateTaxes.FilingStatus;
      taxCtrls.StateWithholdingAllowance = workStateTaxes.WithholdingAllowance || 0;
      taxCtrls.StateAddlAllowances = workStateTaxes.AdditionalAllowances || 0;
      taxCtrls.StateAddlWithholdingType = workStateTaxes.AdditionalWithholdingType.trim() || 'null';
      taxCtrls.StateAddlWithholdingValue = workStateTaxes.AdditionalWithholdingAmount || 0;
    } else {
      taxCtrls.StateFilingStatus = 'S ';
      taxCtrls.StateAddlWithholdingType = 'null';
      taxCtrls.StateWithholdingAllowance = 0;
      taxCtrls.StateAddlAllowances = 0;
      taxCtrls.StateAddlWithholdingValue = 0;
    }

    if (homeStateTaxes !== null) {
      taxCtrls.AddlStateFilingStatus = homeStateTaxes.FilingStatus;
      taxCtrls.AddlStateWithholdingAllowance = homeStateTaxes.WithholdingAllowance || 0;
      taxCtrls.AddlStateAddlAllowances = homeStateTaxes.AdditionalAllowances || 0;
      taxCtrls.AddlStateAddlWithholdingType =
        homeStateTaxes.AdditionalWithholdingType.trim() || 'null';
      taxCtrls.AddlStateAddlWithholdingValue = homeStateTaxes.AdditionalWithholdingAmount || 0;
    }
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: taxCtrls
          })
        })
      })
    );
  }

  @Action(ResetTaxesForm)
  resetTaxesForm(ctx: StateContext<IPersonEditorState>) {
    let state = ctx.getState();
    Object.keys(state.Forms.TaxesForm.model).forEach(key => {
      if (key != 'LocalTypeId') {
        ctx.setState(
          patch<IPersonEditorState>({
            Forms: patch({
              TaxesForm: patch({
                model: patch({
                  [key]: null
                })
              })
            })
          })
        );
        state = ctx.getState();
      }
    });
    return ctx.dispatch(new BuildLocalTaxForm());
  }

  @Action(BuildLocalTaxForm)
  buildLocalTaxForm(ctx: StateContext<IPersonEditorState>) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(clientCode => {
        let editorState = ctx.getState();
        // local taxes are dynamically build anytime resident or works in state changes
        const state =
          editorState.Forms.PersonalDetailsForm.model.State || (editorState.Person?.State ?? '');
        const work =
          editorState.Forms.JobDetailsForm.model.WorksIn || (editorState.Person?.WorksIn ?? '');

        if (state || work) {
          return this.companyService.getLocalTaxConfigs(clientCode, state, work);
        } else {
          ctx.setState(
            patch<IPersonEditorState>({
              Forms: patch({
                TaxesForm: patch({
                  model: patch({
                    Locals: []
                  })
                })
              }),
              CompanyConfigs: patch({
                LocalTaxs: []
              })
            })
          );
          return of([]);
        }
      }),
      tap(configs => {
        let editorState = ctx.getState();
        // local taxes are dynamically build anytime resident or works in state changes
        const state =
          editorState.Forms.PersonalDetailsForm.model.State || (editorState.Person?.State ?? '');
        const work =
          editorState.Forms.JobDetailsForm.model.WorksIn || (editorState.Person?.WorksIn ?? '');
        if (state || work) {
          ctx.setState(
            patch<IPersonEditorState>({
              CompanyConfigs: patch({
                LocalTaxs: configs
              })
            })
          );
          editorState = ctx.getState();
          // construct form
          const taxes = editorState.PersonLists.Taxes;
          ctx.setState(
            patch<IPersonEditorState>({
              Forms: patch({
                TaxesForm: patch({
                  model: patch({
                    Locals: []
                  })
                })
              })
            })
          );
          editorState.CompanyConfigs.LocalTaxs.forEach(config => {
            const locals = taxes.Locals;
            let local = locals ? taxes.Locals.find(x => x.LocalTypeId == config.LocalTypeId) : null;
            ctx.setState(
              patch<IPersonEditorState>({
                Forms: patch({
                  TaxesForm: patch({
                    model: patch({
                      Locals: append([
                        {
                          LocalTypeId: config.LocalTypeId,
                          TaxCode: local ? local.TaxCode : '',
                          FilingStatus: local ? local.FilingStatus : '',
                          WithholdingAllowances: local ? local.WithholdingAllowances : 0,
                          County: null,
                          Municipality: null,
                          SchoolDistrict: null
                        }
                      ])
                    })
                  })
                })
              })
            );
            editorState = ctx.getState();
          });
        }
      })
    );
  }

  @Action(SetEmployeeStateTaxExemptionsByState)
  setEmployeeStateTaxExemptionsByState(ctx: StateContext<IPersonEditorState>) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      map(clientCode => {
        let editorState = ctx.getState();
        const worksIn = PersonEditorState.getEmployeeWorksIn(editorState);
        if (!editorState.Person.Id) {
          if (worksIn != null) {
            this.companyService.getStateTaxExemptions(clientCode, worksIn).subscribe(exemptions => {
              const taxExemptions = new EmployeeTaxExemptions();

              const stateTax = new StateTaxExemption();
              stateTax.EmployeeTaxExemptionState = worksIn;
              stateTax.EmployeeStateTaxExemptions = new StateTaxExemptions();

              for (let exemption of exemptions.Model) {
                if (editorState.ClientStateTaxExemptions.includes(exemption)) {
                  const i = editorState.ClientStateTaxExemptions.indexOf(exemption);
                  stateTax.EmployeeStateTaxExemptions[editorState.StateTaxExemptions[i]] = false;
                }
              }

              taxExemptions.EmployeeStateTaxExemptionsByState = [stateTax];
              ctx.patchState({
                TaxExemptions: taxExemptions
              });
              editorState = ctx.getState();
            });
          }
        } else {
          this.store.dispatch(new SetTaxExemptions());
        }
      })
    );
  }

  @Action(SetTaxExemptions)
  setTaxExemptions(ctx: StateContext<IPersonEditorState>) {
    let state = ctx.getState();
    const exemptions = state.TaxExemptions;
    if (exemptions) {
      const clone = new EmployeeTaxExemptions();

      _.extend(clone, exemptions, {
        J1F1Visa: exemptions.HasVisa ? '1' : '0',
        ExpireDate: formatDatePickerValue(exemptions.VisaExpirationDate)
      });
      ctx.setState(
        patch<IPersonEditorState>({
          Forms: patch({
            TaxesForm: patch({
              model: patch(clone)
            })
          })
        })
      );
      state = ctx.getState();
      const stateExemptions = this.saveService.getStateTaxExemptions(state);
      if (stateExemptions) {
        for (let se of state.StateTaxExemptions) {
          ctx.setState(
            patch<IPersonEditorState>({
              Forms: patch({
                TaxesForm: patch({
                  model: patch({
                    [se]: stateExemptions.EmployeeStateTaxExemptions[se]
                  })
                })
              })
            })
          );
          state = ctx.getState();
        }
      }
    }
  }

  @Action(SetCurrentTabStatus)
  setCurrentFormStatus(ctx: StateContext<IPersonEditorState>, { status }: SetCurrentTabStatus) {
    //We're assuming validity unless we get an explicit 'INVALID' status, because otherwise sometimes
    // the form ends up in a 'PENDING' status on page load
    //and that prevents changing tabs.
    ctx.patchState({
      IsCurrentTabValid: status !== 'INVALID'
    });
  }

  @Action(SetCurrentTabDirty)
  setCurrentTabDirty(ctx: StateContext<IPersonEditorState>, { newValue }: SetCurrentTabDirty) {
    ctx.patchState({
      IsCurrentTabDirty: newValue
    });
  }

  @Action(LoadPerson)
  loadPerson(ctx: StateContext<IPersonEditorState>, { clientCode, personId }: LoadPerson) {
    const state = ctx.getState();
    return this.peopleService.getPerson(clientCode, personId).pipe(
      catchError(err => {
        return ctx.dispatch(new Navigate(['/']));
      }),
      mergeMap(person => {
        if (person) {
          const loc = state.CompanyConfigs.Locations.find(x => x.Id === person.Location);
          ctx.setState(
            patch<IPersonEditorState>({
              Person: person,
              PersonHasFederalId: !!person.FederalIdentifier,
              MaskFederalId: !!person.FederalIdentifier,
              UseNewHireReporting: person.ShouldReportAsNewHire,
              AllowDirectDeposits: loc && loc.AllowDirectDeposit
            })
          );
          return ctx.dispatch(new LoadUniversalPersonLists(clientCode, person.Id)).pipe(
            mergeMap(() => {
              return ctx.dispatch(new LoadPersonalDetailsForm());
            }),
            mergeMap(() => {
              return ctx.dispatch(new LoadJobDetailsForm());
            }),
            mergeMap(() => {
              return ctx.dispatch(new SetTaxes());
            }),
            mergeMap(() => {
              return ctx.dispatch(new SetTaxExemptions());
            }),
            mergeMap(() => {
              return ctx.dispatch(new BuildLocalTaxForm());
            }),
            mergeMap(() => {
              return ctx.dispatch(new SetHeader());
            }),
            mergeMap(() => {
              return ctx.dispatch(new MarkFormsPristine());
            })
          );
        } else {
          return ctx.dispatch(new Navigate(['/']));
        }
      })
    );
  }

  @Action(LoadPersonalDetailsForm)
  loadPersonalDetailsForm(ctx: StateContext<IPersonEditorState>) {
    const person = ctx.getState().Person;
    let postalCode = null;
    if (person.PostalCode != null) {
      postalCode =
        person.PostalCode + (person.PostalCodeExtension ? person.PostalCodeExtension.trim() : null);
    }

    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: {
              EmploymentTypeId: person.EmploymentTypeId ?? EmploymentTypes.W2,
              UseEIN: person.UseEIN ? '1' : '0',
              FederalIdentifier: '',
              VerifyFederalIdentifier: '',
              IsCompany: person.IsCompany,
              FirstName: person.FirstName,
              MiddleName: person.MiddleName,
              LastName: person.LastName,
              Birthday: formatDatePickerValue(person.Birthday),
              PersonalEmailAddress: person.PersonalEmailAddress,
              HomePhoneNumber: person.HomePhoneNumber ? person.HomePhoneNumber.trim() : '',
              MobilePhoneNumber: person.MobilePhoneNumber ? person.MobilePhoneNumber.trim() : '',
              PostalCode: postalCode,
              Gender: person.Gender,
              MaritalStatus: person.MaritalStatus,
              Race: person.Race,
              StreetAddress: person.StreetAddress,
              StreetAddress2: person.StreetAddress2,
              City: person.City,
              State: person.State,
              Country: person.Country,
              IsNonUSAddress: person.IsNonUSAddress
            }
          })
        })
      })
    );
  }

  @Action(LoadJobDetailsForm)
  loadJobDetailsForm(ctx: StateContext<IPersonEditorState>) {
    return this.store.selectOnce(ClientSelectionState.client).pipe(
      tap(client => {
        const state = ctx.getState();
        const person = state.Person;
        const newHire =
          person.Id != null ? person.ShouldReportAsNewHire : client.UseNewHireReporting;
        const loc =
          state.CompanyConfigs.Locations.find(x => x.Id === person.Location) ??
          state.CompanyConfigs.Locations[0];
        ctx.setState(
          patch<IPersonEditorState>({
            Forms: patch({
              JobDetailsForm: patch({
                model: {
                  EmployeeId: person.EmployeeId,
                  HireDate: person.HireDate
                    ? formatDatePickerValue(person.HireDate)
                    : formatDatePickerValue(new Date()),
                  WorksIn: person.WorksIn,
                  OKToAutoPay: person.OKToAutoPay ? '1' : '0',
                  ShouldReportAsNewHire: newHire ? '1' : '0',
                  PayFrequency: person.PayFrequency ?? loc.Calendars[0].Frequency,
                  Location: person.Location,
                  Division: person.Division,
                  Department: person.Department,
                  JobTitle: person.JobTitle,
                  IsSeasonal: person.IsSeasonal,
                  FullTimePartTime: person.FullTimePartTime || 'F',
                  IsOwnerOfficer: person.IsOwnerOfficer,
                  SOCCode: person.SOCCode,
                  OccupationId: person.OccupationId,
                  BasisOfPayment: person.BasisOfPayment,
                  WorkEmailAddress: person.WorkEmailAddress,
                  WorkPhoneNumber: person.WorkPhoneNumber,
                  OriginalHireDate: formatDatePickerValue(person.OriginalHireDate),
                  IncludeNonW2InWorkersComp: person.IncludeNonW2InWorkersComp,
                  WorkersCompCode: person.WorkersCompCode,
                  WorkersCompOwnerStatus: person.WorkersCompOwnerStatus
                    ? person.WorkersCompOwnerStatus.toString()
                    : '1',
                  WorkersCompLimitId: person.WorkersCompLimitId,
                  LaborAllocationTotal: null
                }
              })
            })
          })
        );
      })
    );
  }

  @Action(LoadUniversalEnums)
  loadUniversalEnums(ctx: StateContext<IPersonEditorState>, { clientCode }: LoadUniversalEnums) {
    const obs = [
      this.enumService.get('StateList'),
      this.enumService.get('EmployeeStatuses'),
      this.enumService.get('DeductionTypes')
    ];

    return forkJoin(obs).pipe(
      tap(results => {
        ctx.setState(
          patch<IPersonEditorState>({
            Enums: patch({
              States: results[0],
              EmployeeStatuses: results[1],
              DeductionTypes: results[2]
            })
          })
        );
      })
    );
  }

  @Action(ResetAddressFields)
  resetAddressFields(ctx: StateContext<IPersonEditorState>) {
    const state = ctx.getState();
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              StreetAddress: state.Person.StreetAddress,
              StreetAddress2: state.Person.StreetAddress2,
              City: state.Person.City,
              State: state.Person.State,
              PostalCode: state.Person.PostalCode,
              Country: state.Person.Country,
              IsNonUSAddress: state.Person.IsNonUSAddress
            })
          })
        })
      })
    );
  }

  @Action(ClearSSN)
  clearSSN(ctx: StateContext<IPersonEditorState>) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              FederalIdentifier: '',
              VerifyFederalIdentifier: ''
            })
          })
        }),
        PersonHasFederalId: false
      })
    );
  }

  @Action(ResetPersonalDetailsForm)
  resetPersonalDetailsForm(ctx: StateContext<IPersonEditorState>) {
    ctx.dispatch(new ResetForm({ path: 'personEditor.Forms.PersonalDetailsForm' }));
  }

  @Action(ResetJobDetailsForm)
  resetJobDetailsForm(ctx: StateContext<IPersonEditorState>) {
    ctx.dispatch(new ResetForm({ path: 'personEditor.Forms.JobDetailsForm' }));
  }

  @Action(SetEditorMode)
  setEditorMode(ctx: StateContext<IPersonEditorState>, { mode }: SetEditorMode) {
    ctx.patchState({
      EditorMode: mode
    });
  }

  @Action(SetHeader)
  setHeader(ctx: StateContext<IPersonEditorState>, { nameOnly }: SetHeader) {
    const state = ctx.getState();
    const firstName = state.Forms.PersonalDetailsForm.model.FirstName;
    const lastName = state.Forms.PersonalDetailsForm.model.LastName;
    const empType = state.Forms.PersonalDetailsForm.model.EmploymentTypeId;
    let headerName = '';

    if (!firstName && !lastName) {
      headerName = 'Add Person';
      ctx.setState(
        patch<IPersonEditorState>({
          Header: patch({
            Name: headerName
          })
        })
      );
    } else {
      if (state.Forms.PersonalDetailsForm.model.IsCompany) {
        headerName = '';
      } else if (firstName) {
        headerName = firstName + ' ';
      }

      if (lastName) {
        headerName += lastName;
      }

      // name in header should be updated immedately when name fields change, but only when creating new person
      // everything else should update on continue/save click
      // after we have changed tabs at least once - show emp type and status if available
      let headerEmployeeType = state.Header.EmployeeType;
      if (empType && (!nameOnly || state.HasChangedTabs)) {
        // display employee type
        let name = '';
        if (empType == 1) {
          name = 'W-2';
        } else if (empType == 2) {
          name = '1099';
        } else if (empType == 3) {
          name = 'K-1';
        }
        headerEmployeeType = name;
      }
      let headerStatus = state.Header.Status;
      if (!nameOnly || state.HasChangedTabs) {
        // display latest status
        if (state.PersonLists.Statuses && state.PersonLists.Statuses[0]) {
          const s = state.Enums.EmployeeStatuses.find(
            x => x.Id == state.PersonLists.Statuses[0].StatusId.toString()
          );
          headerStatus = s ? s.Value : '';
        } else {
          headerStatus = 'Active';
        }
      }
      ctx.setState(
        patch<IPersonEditorState>({
          Header: patch({
            Name: headerName,
            EmployeeType: headerEmployeeType,
            Status: headerStatus
          })
        })
      );
    }
  }

  @Action(SetFederalIdChanged)
  setFederalIdChanged(ctx: StateContext<IPersonEditorState>, { newValue }: SetFederalIdChanged) {
    ctx.patchState({
      FederalIdChanged: newValue
    });
  }

  @Action(SetMaskFederalId)
  setMaskFederalId(ctx: StateContext<IPersonEditorState>, { newValue }: SetMaskFederalId) {
    ctx.patchState({
      MaskFederalId: newValue
    });
  }

  @Action(SetPersonHasFederalId)
  setPersonHasFederalId(
    ctx: StateContext<IPersonEditorState>,
    { newValue, overrideSaveSafety }: SetPersonHasFederalId
  ) {
    const state = ctx.getState();
    if (!state.SaveInProgress || overrideSaveSafety) {
      ctx.patchState({
        PersonHasFederalId: newValue
      });
    }
  }

  @Action(UpdateEmployeeDeduction)
  updateDeduction(ctx: StateContext<IPersonEditorState>, { deduction }: UpdateEmployeeDeduction) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Deductions: updateItem<EmployeeDeduction>(x => x.Id === deduction.Id, deduction)
        })
      })
    );
  }

  @Action(ReloadEmployeeDeductions)
  reloadEmployeeDeductions(
    ctx: StateContext<IPersonEditorState>,
    { clientCode }: ReloadEmployeeDeductions
  ) {
    const state = ctx.getState();
    return this.peopleService.getEmployeeDeductions(clientCode, state.Person.Id).pipe(
      tap(deductions => {
        ctx.setState(
          patch<IPersonEditorState>({
            PersonLists: patch({
              Deductions: deductions
            })
          })
        );
      })
    );
  }

  @Action(AddEmployeeDeduction)
  addEmployeeDeduction(ctx: StateContext<IPersonEditorState>, { deduction }: AddEmployeeDeduction) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Deductions: append([deduction])
        })
      })
    );
  }

  @Action(ReloadDirectDeposits)
  reloadDirectDeposits(ctx: StateContext<IPersonEditorState>) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(clientCode => {
        const state = ctx.getState();
        return this.peopleService.getDirectDeposits(clientCode, state.Person.Id).pipe(
          tap(deposits => {
            ctx.setState(
              patch<IPersonEditorState>({
                PersonLists: patch({
                  DirectDeposits: deposits
                })
              })
            );
          })
        );
      })
    );
  }

  @Action(ReloadEmployeeEarnings)
  reloadEmployeeEarnings(
    ctx: StateContext<IPersonEditorState>,
    { clientCode }: ReloadEmployeeEarnings
  ) {
    const state = ctx.getState();
    return this.peopleService.getEmployeeEarnings(clientCode, state.Person.Id).pipe(
      tap(earnings => {
        ctx.setState(
          patch<IPersonEditorState>({
            PersonLists: patch({
              Earnings: earnings
            })
          })
        );
      })
    );
  }

  @Action(SetEmployeeEarnings)
  setEmployeeEarnings(ctx: StateContext<IPersonEditorState>, { earnings }: SetEmployeeEarnings) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Earnings: earnings
        })
      })
    );
  }

  @Action(UpdateEmployeeEarning)
  updateEmployeeEarning(ctx: StateContext<IPersonEditorState>, { earning }: UpdateEmployeeEarning) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Earnings: updateItem<EmployeeEarning>(x => x.Id === earning.Id, earning)
        })
      })
    );
  }

  @Action(AddEmployeeEarning)
  addEmployeeEarning(ctx: StateContext<IPersonEditorState>, { earning }: AddEmployeeEarning) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Earnings: append([earning])
        })
      })
    );
  }

  @Action(LoadUniversalPersonLists)
  loadUniversalPersonLists(
    ctx: StateContext<IPersonEditorState>,
    { clientCode, personId, validate }: LoadUniversalPersonLists
  ) {
    const state = ctx.getState();
    const obs = [
      this.peopleService.getStatuses(clientCode, personId ?? state.Person.Id),
      this.peopleService.getEmergencyContacts(clientCode, state.Person.Id),
      this.peopleService.getLaborAllocations(clientCode, state.Person.Id),
      this.peopleService.getEmployeePTOs(clientCode, state.Person.Id),
      this.peopleService.getEmployeeTaxes(clientCode, state.Person.Id),
      this.peopleService.getEmployeeEarnings(clientCode, state.Person.Id),
      this.peopleService.getEmployeeDeductions(clientCode, state.Person.Id),
      this.peopleService.getEmployeeTaxExemptions(clientCode, state.Person.Id),
      this.peopleService.getDirectDeposits(clientCode, state.Person.Id)
    ];
    return forkJoin(obs).pipe(
      tap((results: any[]) => {
        ctx.setState(
          patch<IPersonEditorState>({
            TaxExemptions: results[7].Model,
            PersonLists: patch({
              Statuses: results[0],
              EmergencyContacts: results[1],
              LaborAllocations: results[2],
              EmployeePTOs: results[3],
              Taxes: results[4],
              Earnings: results[5],
              Deductions: results[6],
              DirectDeposits: results[8]
            })
          })
        );
        if (validate) {
          this.validationService.checkPayrollValidity(ctx.getState());
        }
      })
    );
  }

  @Action(RefreshEmployeeStatuses)
  refreshEmployeeStatuses(
    ctx: StateContext<IPersonEditorState>,
    { clientCode }: RefreshEmployeeStatuses
  ) {
    const state = ctx.getState();
    return this.peopleService.getStatuses(clientCode, state.Person.Id).pipe(
      tap(statuses => {
        ctx.setState(
          patch<IPersonEditorState>({
            PersonLists: patch({
              Statuses: statuses
            })
          })
        );
      })
    );
  }

  @Action(ClearEmploymentTypeCollections)
  clearEmploymentTypeCollections(ctx: StateContext<IPersonEditorState>) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Earnings: [],
          Deductions: [],
          Taxes: new EmployeeTaxes()
        }),
        TaxExemptions: new EmployeeTaxExemptions(),
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              IncludeNonW2InWorkersComp: null,
              WorkersCompCode: null,
              WorkersCompOwnerStatus: null,
              WorkersCompLimitId: null
            })
          })
        }),
        Person: patch({
          IncludeNonW2InWorkersComp: null,
          WorkersCompCode: null,
          WorkersCompOwnerStatus: null,
          WorkersCompLimitId: null
        })
      })
    );
    return ctx.dispatch(new ResetForm({ path: 'personEditor.Forms.TaxesForm' }));
  }

  @Action(ClearEmployeeStatuses)
  clearEmployeeStatuses(ctx: StateContext<IPersonEditorState>) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Statuses: []
        })
      })
    );
  }

  @Action(SetEmployeeDeductions)
  setEmployeeDeductions(
    ctx: StateContext<IPersonEditorState>,
    { deductions }: SetEmployeeDeductions
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Deductions: deductions
        })
      })
    );
  }

  @Action(LoadUniversalCompanyConfigs)
  loadUniversalCompanyConfigs(
    ctx: StateContext<IPersonEditorState>,
    { clientCode }: LoadUniversalCompanyConfigs
  ) {
    const obs = [
      this.companyService.getLocations(clientCode),
      this.companyService.getEarnings(clientCode),
      this.companyService.getTaxConfigs(clientCode, false),
      this.companyService.getJobCodes(clientCode),
      this.companyService.getWorkersCompCodes(clientCode),
      this.companyService.getDeductions(clientCode),
      this.companyService.getPTOConfigs(clientCode)
    ];
    return forkJoin(obs).pipe(
      tap((results: any[]) => {
        const state = ctx.getState();
        let allowDirectDeposits = false;
        if (state.Person != null) {
          const loc = results[0].find(x => x.Id === state.Person.Location);
          allowDirectDeposits = loc && loc.AllowDirectDeposit;
        }
        ctx.setState(
          patch<IPersonEditorState>({
            CompanyConfigs: patch({
              Locations: results[0],
              Earnings: results[1],
              TaxConfigs: results[2],
              JobCodes: results[3],
              WorkersCompCodes: results[4],
              Deductions: results[5],
              PTOConfigs: results[6]
            }),
            AllowDirectDeposits: allowDirectDeposits
          })
        );
      })
    );
  }

  @Action(SetReadOnly)
  setReadOnly(ctx: StateContext<IPersonEditorState>, { newValue }: SetReadOnly) {
    ctx.patchState({
      ReadOnly: newValue
    });
  }

  @Action(ClearEmergencyContacts)
  clearEmergencyContacts(ctx: StateContext<IPersonEditorState>) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          EmergencyContacts: []
        })
      })
    );
  }

  @Action(RefreshPersonalDetailsLists)
  refreshPersonalDetailsLists(
    ctx: StateContext<IPersonEditorState>,
    { clientCode }: RefreshPersonalDetailsLists
  ) {
    const state = ctx.getState();
    const obs = [
      this.peopleService.getEmergencyContacts(clientCode, state.Person.Id),
      this.peopleService.getLaborAllocations(clientCode, state.Person.Id)
    ];
    return forkJoin(obs).pipe(
      tap((results: any[]) => {
        ctx.setState(
          patch<IPersonEditorState>({
            PersonLists: patch({
              EmergencyContacts: results[0]
            })
          })
        );
      })
    );
  }

  @Action(SetEmergencyContacts)
  setEmergencyContacts(ctx: StateContext<IPersonEditorState>, { contacts }: SetEmergencyContacts) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          EmergencyContacts: contacts
        })
      })
    );
  }

  @Action(AddEmergencyContact)
  addEmergencyContact(ctx: StateContext<IPersonEditorState>, { contact }: AddEmergencyContact) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          EmergencyContacts: append([contact])
        })
      })
    );
  }

  @Action(RemoveEmergencyContact)
  removeEmergencyContact(
    ctx: StateContext<IPersonEditorState>,
    { contact }: RemoveEmergencyContact
  ) {
    const state = ctx.getState();
    const index = state.PersonLists.EmergencyContacts.indexOf(contact);
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          EmergencyContacts: removeItem<EmergencyContact>(index)
        })
      })
    );
  }

  @Action(EditEmergencyContact)
  editEmergencyContact(ctx: StateContext<IPersonEditorState>, { contact }: EditEmergencyContact) {
    const state = ctx.getState();
    const index = state.PersonLists.EmergencyContacts.indexOf(contact);
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          EmergencyContacts: updateItem(index, contact)
        })
      })
    );
  }

  @Action(RefreshPerson)
  refreshPerson(ctx: StateContext<IPersonEditorState>, { clientCode }: RefreshPerson) {
    const state = ctx.getState();
    return this.peopleService.getPerson(clientCode, state.Person.Id).pipe(
      tap(person => {
        ctx.patchState({
          Person: person
        });
      })
    );
  }

  @Action(SetPayFrequencyDetails)
  setPayFrequencyDetails(
    ctx: StateContext<IPersonEditorState>,
    { payFrequency, calendarId }: SetPayFrequencyDetails
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Person: patch({
          PayFrequency: payFrequency,
          CalendarId: calendarId
        })
      })
    );
  }
  @Action(SetCalendarId)
  setCalendarId(ctx: StateContext<IPersonEditorState>, { calendarId }: SetCalendarId) {
    ctx.setState(
      patch<IPersonEditorState>({
        Person: patch({
          CalendarId: calendarId
        })
      })
    );
  }

  @Action(AddJobCode)
  addJobCode(ctx: StateContext<IPersonEditorState>, { jobCode }: AddJobCode) {
    ctx.setState(
      patch<IPersonEditorState>({
        CompanyConfigs: patch({
          JobCodes: append([jobCode])
        })
      })
    );
  }
  @Action(SetLaborAllocations)
  setLaborAllocations(
    ctx: StateContext<IPersonEditorState>,
    { laborAllocations }: SetLaborAllocations
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          LaborAllocations: laborAllocations
        })
      })
    );
  }

  @Action(AddLaborAllocation)
  addLaborAllocation(
    ctx: StateContext<IPersonEditorState>,
    { laborAllocation }: AddLaborAllocation
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          LaborAllocations: append([laborAllocation])
        })
      })
    );
  }

  @Action(RemoveLaborAllocation)
  removeLaborAllocation(
    ctx: StateContext<IPersonEditorState>,
    { laborAllocation }: RemoveLaborAllocation
  ) {
    const state = ctx.getState();
    const index = state.PersonLists.LaborAllocations.indexOf(laborAllocation);
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          LaborAllocations: removeItem<EmployeeLaborAllocation>(index)
        })
      })
    );
  }

  @Action(UpdateLaborAllocation)
  editLaborAllocation(
    ctx: StateContext<IPersonEditorState>,
    { laborAllocation, percentageOfGrossPay, jobCodeId }: UpdateLaborAllocation
  ) {
    const state = ctx.getState();
    const index = state.PersonLists.LaborAllocations.indexOf(laborAllocation);
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          LaborAllocations: updateItem(
            index,
            patch({
              JobCodeId: jobCodeId,
              PercentageOfGrossPay: percentageOfGrossPay
            })
          )
        })
      })
    );
  }

  @Action(SetAllowDirectDeposit)
  setAllowDirectDeposit(
    ctx: StateContext<IPersonEditorState>,
    { allowDirectDeposit }: SetAllowDirectDeposit
  ) {
    ctx.patchState({
      AllowDirectDeposits: allowDirectDeposit
    });
  }

  @Action(AddEmployeeStatus)
  addEmployeeStatuses(ctx: StateContext<IPersonEditorState>, { status }: AddEmployeeStatus) {
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Statuses: insertItem<EmployeeStatus>(status, 0)
        })
      })
    );
    return ctx.dispatch(new PersonStatusChanged());
  }

  @Action(EditEmployeeStatus)
  editEmployeeStatus(ctx: StateContext<IPersonEditorState>, { status }: EditEmployeeStatus) {
    const state = ctx.getState();
    const index = state.PersonLists.Statuses.indexOf(status);
    ctx.setState(
      patch<IPersonEditorState>({
        PersonLists: patch({
          Statuses: updateItem(
            x => x.StatusId === status.StatusId && x.SequenceNumber === status.SequenceNumber,
            status
          )
        })
      })
    );
  }

  @Action(SetUseNewHireReporting)
  setUseNewHireReporting(
    ctx: StateContext<IPersonEditorState>,
    { useNewHireReporting }: SetUseNewHireReporting
  ) {
    ctx.setState(
      patch({
        UseNewHireReporting: useNewHireReporting
      })
    );
  }

  @Action(SetOccupationChanged)
  setOccupationChanged(ctx: StateContext<IPersonEditorState>, { newValue }: SetOccupationChanged) {
    ctx.patchState({
      OccupationChanged: newValue
    });
  }

  @Action(PersonStatusChanged)
  personStatusChanged(ctx: StateContext<IPersonEditorState>) {
    const state = ctx.getState();
    this.validationService.checkPayrollValidity(state);
  }

  @Action(SetSaveInProgress)
  setSaveInProgress(ctx: StateContext<IPersonEditorState>, { newValue }: SetSaveInProgress) {
    ctx.patchState({
      SaveInProgress: newValue
    });
  }

  @Action(SavePerson)
  savePerson(
    ctx: StateContext<IPersonEditorState>,
    { clientCode, tabElemIds, activeTabId }: SavePerson
  ) {
    let state = ctx.getState();

    let p = structuredClone(state.Person);
    const _person = structuredClone(state.Person); // audit shadow copy
    const federalIdChanged = state.FederalIdChanged;
    const originalTaxExemptions = structuredClone(state.TaxExemptions);
    const exemptions = this.saveService.GetTaxExemptions(state);

    // set person model & collections data from reactive form inputs
    p = this.saveService.SavePersonalDetails(p, state);
    //set job details
    p = this.saveService.SaveJobDetails(p, state);
    if (!p.Id) {
      let taxes = state.PersonLists.Taxes;
      if (!this.validationService.isTabDisabledAtIndex(4, tabElemIds)) {
        taxes = this.saveService.setTaxDataModel(
          p,
          state.Forms.JobDetailsForm.model,
          state.Forms.TaxesForm.model,
          state.PersonLists.Taxes,
          state.CompanyConfigs.TaxConfigs
        );
      }

      this.companyService
        .getLocalTaxConfigs(clientCode, p.State, p.WorksIn)
        .subscribe(localTaxes => {
          if (localTaxes?.length > 0) {
            this.modalService.showAlertModal(
              'Employee may need local taxes set up for this profile',
              ModalStates.Warning
            );
          }
        });

      return this.saveService
        .CreatePerson(
          new FullPerson(
            p,
            state.PersonLists.Deductions,
            state.PersonLists.DirectDeposits,
            state.PersonLists.Earnings,
            state.PersonLists.EmergencyContacts,
            state.PersonLists.LaborAllocations,
            state.PersonLists.Statuses,
            taxes,
            exemptions
          ),
          ctx,
          clientCode
        )
        .pipe(
          mergeMap(() => {
            const obs = [
              ctx.dispatch(new SetHeader()),
              ctx.dispatch(new SetPersonHasFederalId(true, true)),
              ctx.dispatch(new SetShowVerifyFederalId(false)),
              ctx.dispatch(new SetMaskFederalId(true)),
              ctx.dispatch(new SetFederalIdChanged(false)),
              ctx.dispatch(new MarkFormsPristine()),
              ctx.dispatch(new RefreshPersonLists())
            ];
            return forkJoin(obs);
          })
        );
    } else if (activeTabId === 'personal-details-tab' || activeTabId === 'job-details-tab') {
      state = ctx.getState();
      return this.peopleService.updatePerson(clientCode, p).pipe(
        mergeMap((response: ApiResponse<Person>) => {
          ctx.patchState({
            Person: response.Model,
            PersonHasFederalId: response.Model.FederalIdentifier != null
          });
          state = ctx.getState();
          const msg = FlattenResponseMessages(response.Messages, true);

          if (msg?.Body) {
            ctx.patchState({
              ModalStyle: GetResponseModalStyle(response.Messages),
              AlertModalMessage: msg
            });
          }

          // record changes in the audit db
          const skip = [
            'FederalIdentifier',
            'IntegrityHash',
            'InvalidToPay',
            'InvalidToPayDetails',
            'LastPay',
            'ClientCode'
          ];

          if (!state.OccupationChanged) {
            skip.push('OccupationId');
          }

          const deltas = getObjectDeltas(_person, state.Person, skip);
          // we never want fed ids in the audit db; only record if it was changed
          if (federalIdChanged) {
            deltas.FederalIdentifier = '{ "oldVal": "****", "newVal": "changed" }';
          }

          const data = {
            PersonId: state.Person.Id,
            ClientCode: clientCode,
            EmployeeNumber: state.Person.EmployeeId,
            EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
          };

          this.auditService.auditEmployee('employee-updated', data, deltas);

          // refetch employee taxes incase there is a mismatch now
          return this.peopleService.getEmployeeTaxes(clientCode, state.Person.Id);
        }),
        tap((response: EmployeeTaxes) => {
          ctx.setState(
            patch<IPersonEditorState>({
              PersonLists: patch({
                Taxes: response
              })
            })
          );
        }),
        mergeMap(() => {
          return ctx.dispatch(new SetHeader());
        }),
        mergeMap(() => {
          return ctx.dispatch(new SetMaskFederalId(true));
        }),
        mergeMap(() => {
          return ctx.dispatch(new SetPersonHasFederalId(!!p.FederalIdentifier, true));
        }),
        mergeMap(() => {
          return ctx.dispatch(new SetShowVerifyFederalId(false));
        }),
        mergeMap(() => {
          return ctx.dispatch(new SetFederalIdChanged(false));
        }),
        mergeMap(() => {
          return ctx.dispatch(new MarkFormsPristine());
        })
      );
    } else if (activeTabId === 'taxes-tab') {
      // audit shadow copy
      const _taxes = {
        FedAndState: [],
        Locals: []
      };
      ctx.setState(
        patch<IPersonEditorState>({
          PersonLists: patch({
            Taxes: patch({
              Locals: state.PersonLists.Taxes.Locals || []
            })
          })
        })
      );
      state = ctx.getState();
      state.PersonLists.Taxes.FedAndState.forEach(t => _taxes.FedAndState.push(structuredClone(t)));
      state.PersonLists.Taxes.Locals.forEach(l => _taxes.Locals.push(structuredClone(l)));
      let updatedTaxes = structuredClone(state.PersonLists.Taxes);
      if (!this.validationService.isTabDisabledAtIndex(4, tabElemIds)) {
        updatedTaxes = this.saveService.setTaxDataModel(
          p,
          state.Forms.JobDetailsForm.model,
          state.Forms.TaxesForm.model,
          state.PersonLists.Taxes,
          state.CompanyConfigs.TaxConfigs
        );
      }
      return this.peopleService.updateEmployeeTaxes(clientCode, state.Person.Id, updatedTaxes).pipe(
        mergeMap((response: ApiResponse<EmployeeTaxes>) => {
          ctx.setState(
            patch<IPersonEditorState>({
              PersonLists: patch({
                Taxes: response.Model
              })
            })
          );
          state = ctx.getState();
          this.toastService.show('Save successful');

          const msg = FlattenResponseMessages(response.Messages, true);

          if (msg?.Body) {
            this.modalService.showAlertModal(msg, GetResponseModalStyle(response.Messages));
          }

          // record changes in the audit db
          for (let i = 0; i < state.PersonLists.Taxes.FedAndState.length; i++) {
            // FIXME: check if this.taxes.FedAndState[i].StateAbbreviation is undefined
            const contextName =
              state.PersonLists.Taxes.FedAndState[i].StateAbbreviation != 'FED'
                ? `State > ${state.PersonLists.Taxes.FedAndState[i].StateAbbreviation}`
                : 'Federal';

            const deltas = getObjectDeltas(
              _taxes.FedAndState[i],
              state.PersonLists.Taxes.FedAndState[i],
              ['IntegrityHash'],
              contextName
            );
            if (Object.keys(deltas).length > 0) {
              this.auditService.auditEmployee(
                `employee-tax-updated`,
                {
                  ClientCode: clientCode,
                  PersonId: state.Person.Id,
                  EmployeeNumber: state.Person.EmployeeId,
                  EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
                },
                deltas
              );
            }
          }

          this._auditLocalTaxes(_taxes, state, clientCode);

          if (response.RefreshRequired) {
            return ctx.dispatch(new LoadPerson(clientCode, state.Person.Id)).pipe(
              mergeMap(() => {
                return this.peopleService.updateEmployeeTaxExemptions(
                  clientCode,
                  state.Person.Id,
                  exemptions
                );
              })
            );
          } else {
            return this.peopleService.updateEmployeeTaxExemptions(
              clientCode,
              state.Person.Id,
              exemptions
            );
          }
        }),
        tap((response: ApiResponse<EmployeeTaxExemptions>) => {
          ctx.setState(
            patch<IPersonEditorState>({
              TaxExemptions: response.Model
            })
          );
          state = ctx.getState();
          // record changes in the audit db
          var deltas = getObjectDeltas(originalTaxExemptions, state.TaxExemptions, [
            'IntegrityHash'
          ]);

          var stateDeltas = this.saveService.getStateTaxExemptionDeltas(
            originalTaxExemptions,
            state.TaxExemptions
          );

          deltas = { ...stateDeltas, ...deltas };

          if (Object.keys(deltas).length > 0) {
            this.auditService.auditEmployee(
              `employee-tax-exemption-updated`,
              {
                ClientCode: clientCode,
                PersonId: state.Person.Id,
                EmployeeNumber: state.Person.EmployeeId,
                EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
              },
              deltas
            );
          }
        }),
        mergeMap(() => {
          const obs = [
            ctx.dispatch(new SetHeader()),
            ctx.dispatch(new SetPersonHasFederalId(!!p.FederalIdentifier, true)),
            ctx.dispatch(new SetShowVerifyFederalId(false)),
            ctx.dispatch(new MarkFormsPristine()),
            ctx.dispatch(new SetCurrentTabDirty(false))
          ];
          return forkJoin(obs);
        })
      );
    }
    return of();
  }

  @Action(ClearAlertModal)
  private clearAlertModal(ctx: StateContext<IPersonEditorState>) {
    ctx.patchState({
      ModalStyle: ModalStates.Warning,
      AlertModalMessage: null
    });
  }

  private _auditLocalTaxes(_shadow, state: IPersonEditorState, clientCode: string) {
    const locals = state.PersonLists.Taxes.Locals || [];

    // newly deleted records exist in _shadow but not this.taxes
    _shadow.Locals.forEach(x => {
      const match = state.PersonLists.Taxes.Locals.find(t => t.LocalTypeId === x.LocalTypeId);
      if (!match) {
        // pass newLocal: null for deleted items
        this._auditLocalTaxDeltas(null, x, state, clientCode);
      }
    });

    // newly added records exist in this.taxes but not _shadow
    locals.forEach(x => {
      const match = _shadow.Locals.find(t => t.LocalTypeId === x.LocalTypeId);
      // pass oldLocal: null for newly created items
      this._auditLocalTaxDeltas(x, match || null, state, clientCode);
    });
  }

  private _auditLocalTaxDeltas(newLocal, oldLocal, state: IPersonEditorState, clientCode: string) {
    const localTypeId = newLocal ? newLocal.LocalTypeId : oldLocal.LocalTypeId;
    const contextName = state.CompanyConfigs.LocalTaxs.find(
      x => x.LocalTypeId == localTypeId
    ).TaxLabel;
    const deltas = getObjectDeltas(
      oldLocal,
      newLocal,
      ['IntegrityHash', 'LocalTypeId'],
      contextName
    );
    if (Object.keys(deltas).length > 0) {
      this.auditService.auditEmployee(
        `employee-tax-updated`,
        {
          ClientCode: clientCode,
          PersonId: state.Person.Id,
          EmployeeNumber: state.Person.EmployeeId,
          EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
        },
        deltas
      );
    }
  }

  private static _isDeductionInDefaultState(deduction: EmployeeDeduction) {
    return (
      deduction.IsActive == true &&
      deduction.LoanBalance == 0 &&
      ['', '0'].includes(deduction.DisposableLimitType) &&
      deduction.DisposableLimit == 0 &&
      deduction.AnnualLimit == 0 &&
      deduction.LoanId == '' &&
      deduction.SelectedThirdPartyPayee == 0 &&
      deduction.CaseNumber == '' &&
      deduction.EmployerOffersFamilyMedIns == false
    );
  }

  //JOB DETAILS FORM ACTIONS

  @Action(SetJobDetailsFormState)
  setJobDetailsFormState(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetJobDetailsFormState
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            status: payload
          })
        })
      })
    );
  }

  @Action(SetEmployeeId)
  setEmployeeId(ctx: StateContext<IPersonEditorState>, { payload }: SetEmployeeId) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              EmployeeId: payload
            })
          })
        })
      })
    );
  }

  @Action(SetHireDate)
  setHireDate(ctx: StateContext<IPersonEditorState>, { payload }: SetHireDate) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              HireDate: payload
            })
          })
        })
      })
    );
  }

  @Action(SetShouldReportAsNewHire)
  setShouldReportAsNewHire(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetShouldReportAsNewHire
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              ShouldReportAsNewHire: payload
            })
          })
        })
      })
    );
  }

  @Action(SetWorksIn)
  setWorksIn(ctx: StateContext<IPersonEditorState>, { payload }: SetWorksIn) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              WorksIn: payload
            })
          })
        })
      })
    );
  }

  @Action(SetOKToAutoPay)
  setOKToAutoPay(ctx: StateContext<IPersonEditorState>, { payload }: SetOKToAutoPay) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              OKToAutoPay: payload
            })
          })
        })
      })
    );
  }

  @Action(SetPayFrequency)
  setPayFrequency(ctx: StateContext<IPersonEditorState>, { payload }: SetPayFrequency) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              PayFrequency: payload
            })
          })
        })
      })
    );
  }

  @Action(SetLocation)
  setLocation(ctx: StateContext<IPersonEditorState>, { payload }: SetLocation) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              Location: payload
            })
          })
        })
      })
    );
  }

  @Action(SetDivision)
  setDivision(ctx: StateContext<IPersonEditorState>, { payload }: SetDivision) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              Division: payload
            })
          })
        })
      })
    );
  }

  @Action(SetDepartment)
  setDepartment(ctx: StateContext<IPersonEditorState>, { payload }: SetDepartment) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              Department: payload
            })
          })
        })
      })
    );
  }

  @Action(SetJobTitle)
  setJobTitle(ctx: StateContext<IPersonEditorState>, { payload }: SetJobTitle) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              JobTitle: payload
            })
          })
        })
      })
    );
  }

  @Action(SetIsSeasonal)
  setIsSeasonal(ctx: StateContext<IPersonEditorState>, { payload }: SetIsSeasonal) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              IsSeasonal: payload
            })
          })
        })
      })
    );
  }

  @Action(SetFullTimePartTime)
  setFullTimePartTime(ctx: StateContext<IPersonEditorState>, { payload }: SetFullTimePartTime) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              FullTimePartTime: payload
            })
          })
        })
      })
    );
  }

  @Action(SetIsOwnerOfficer)
  setIsOwnerOfficer(ctx: StateContext<IPersonEditorState>, { payload }: SetIsOwnerOfficer) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              IsOwnerOfficer: payload
            })
          })
        })
      })
    );
  }

  @Action(SetSOCCode)
  setSOCCode(ctx: StateContext<IPersonEditorState>, { payload }: SetSOCCode) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              SOCCode: payload
            })
          })
        })
      })
    );
  }

  @Action(SetOccupationId)
  setOccupationId(ctx: StateContext<IPersonEditorState>, { payload }: SetOccupationId) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              OccupationId: payload
            })
          })
        })
      })
    );
  }

  @Action(SetBasisOfPayment)
  setBasisOfPayment(ctx: StateContext<IPersonEditorState>, { payload }: SetBasisOfPayment) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              BasisOfPayment: payload
            })
          })
        })
      })
    );
  }

  @Action(SetWorkEmail)
  setWorkEmail(ctx: StateContext<IPersonEditorState>, { payload }: SetWorkEmail) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              WorkEmailAddress: payload
            })
          })
        })
      })
    );
  }

  @Action(SetWorkPhoneNumber)
  setWorkPhoneNumber(ctx: StateContext<IPersonEditorState>, { payload }: SetWorkPhoneNumber) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              WorkPhoneNumber: payload
            })
          })
        })
      })
    );
  }

  @Action(SetOriginalHireDate)
  setOriginalHireDate(ctx: StateContext<IPersonEditorState>, { payload }: SetOriginalHireDate) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              OriginalHireDate: payload
            })
          })
        })
      })
    );
  }

  @Action(SetIncludeNonW2InWorkersComp)
  setIncludeNonW2InWorkersComp(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetIncludeNonW2InWorkersComp
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              IncludeNonW2InWorkersComp: payload
            })
          })
        })
      })
    );
  }

  @Action(SetWorkersCompCode)
  setWorkersCompCode(ctx: StateContext<IPersonEditorState>, { payload }: SetWorkersCompCode) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              WorkersCompCode: payload
            })
          })
        })
      })
    );
  }

  @Action(SetWorkersCompOwnerStatus)
  setWorkersCompOwnerStatus(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetWorkersCompOwnerStatus
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              WorkersCompOwnerStatus: payload
            })
          })
        })
      })
    );
  }

  @Action(SetWorkersCompLimitId)
  setWorkersCompLimitId(ctx: StateContext<IPersonEditorState>, { payload }: SetWorkersCompLimitId) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              WorkersCompLimitId: payload
            })
          })
        })
      })
    );
  }

  @Action(SetLaborAllocationTotal)
  setLaborAllocationTotal(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetLaborAllocationTotal
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          JobDetailsForm: patch({
            model: patch({
              LaborAllocationTotal: payload
            })
          })
        })
      })
    );
  }

  //PERSONAL DETAILS FORM ACTIONS

  @Action(SetPersonalDetailsFormState)
  setPersonalDetailsFormState(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetPersonalDetailsFormState
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            status: payload
          })
        })
      })
    );
  }

  @Action(SetEmploymentTypeId)
  setEmploymentTypeId(ctx: StateContext<IPersonEditorState>, { payload }: SetEmploymentTypeId) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              EmploymentTypeId: payload
            })
          })
        })
      })
    );
  }

  @Action(SetUseEIN)
  setUseEIN(ctx: StateContext<IPersonEditorState>, { payload }: SetUseEIN) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              UseEIN: payload
            })
          })
        })
      })
    );
  }

  @Action(SetFederalIdentifier)
  setFederalIdentifier(ctx: StateContext<IPersonEditorState>, { payload }: SetFederalIdentifier) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              FederalIdentifier: payload
            })
          })
        })
      })
    );
  }

  @Action(SetTabHasChanged)
  setTabhasChanged(ctx: StateContext<IPersonEditorState>) {
    ctx.patchState({
      HasChangedTabs: true
    });
  }

  @Action(SetVerifyFederalIdentifier)
  setVerifyFederalIdentifier(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetVerifyFederalIdentifier
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              VerifyFederalIdentifier: payload
            })
          })
        })
      })
    );
  }

  @Action(SetIsCompany)
  setIsCompany(ctx: StateContext<IPersonEditorState>, { payload }: SetIsCompany) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              IsCompany: payload
            })
          })
        })
      })
    );
  }

  @Action(SetFirstName)
  setFirstName(ctx: StateContext<IPersonEditorState>, { payload }: SetFirstName) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              FirstName: payload
            })
          })
        })
      })
    );
  }

  @Action(SetMiddleName)
  setMiddleName(ctx: StateContext<IPersonEditorState>, { payload }: SetMiddleName) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              MiddleName: payload
            })
          })
        })
      })
    );
  }

  @Action(SetLastName)
  setLastName(ctx: StateContext<IPersonEditorState>, { payload }: SetLastName) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              LastName: payload
            })
          })
        })
      })
    );
  }

  @Action(SetBirthday)
  setBirthday(ctx: StateContext<IPersonEditorState>, { payload }: SetBirthday) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              Birthday: payload
            })
          })
        })
      })
    );
  }

  @Action(SetPersonalEmailAddress)
  setPersonalEmailAddress(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetPersonalEmailAddress
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              PersonalEmailAddress: payload
            })
          })
        })
      })
    );
  }

  @Action(SetHomePhoneNumber)
  setHomePhoneNumber(ctx: StateContext<IPersonEditorState>, { payload }: SetHomePhoneNumber) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              HomePhoneNumber: payload
            })
          })
        })
      })
    );
  }

  @Action(SetMobilePhoneNumber)
  setMobilePhoneNumber(ctx: StateContext<IPersonEditorState>, { payload }: SetMobilePhoneNumber) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              MobilePhoneNumber: payload
            })
          })
        })
      })
    );
  }

  @Action(SetGender)
  setGender(ctx: StateContext<IPersonEditorState>, { payload }: SetGender) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              Gender: payload
            })
          })
        })
      })
    );
  }

  @Action(SetMaritalStatus)
  setMaritalStatus(ctx: StateContext<IPersonEditorState>, { payload }: SetMaritalStatus) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              MaritalStatus: payload
            })
          })
        })
      })
    );
  }

  @Action(SetRace)
  setRace(ctx: StateContext<IPersonEditorState>, { payload }: SetRace) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              Race: payload
            })
          })
        })
      })
    );
  }

  @Action(SetStreetAddress)
  setStreetAddress(ctx: StateContext<IPersonEditorState>, { payload }: SetStreetAddress) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              StreetAddress: payload
            })
          })
        })
      })
    );
  }

  @Action(SetStreetAddress2)
  setStreetAddress2(ctx: StateContext<IPersonEditorState>, { payload }: SetStreetAddress2) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              StreetAddress2: payload
            })
          })
        })
      })
    );
  }

  @Action(SetCity)
  setCity(ctx: StateContext<IPersonEditorState>, { payload }: SetCity) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              City: payload
            })
          })
        })
      })
    );
  }

  @Action(SetState)
  setState(ctx: StateContext<IPersonEditorState>, { payload }: SetState) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              State: payload
            })
          })
        })
      })
    );
  }

  @Action(SetPostalCode)
  setPostalCode(ctx: StateContext<IPersonEditorState>, { payload }: SetPostalCode) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              PostalCode: payload
            })
          })
        })
      })
    );
  }

  @Action(SetCountry)
  setCountry(ctx: StateContext<IPersonEditorState>, { payload }: SetCountry) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              Country: payload
            })
          })
        })
      })
    );
  }

  @Action(SetIsNonUSAddress)
  setIsNonUSAddress(ctx: StateContext<IPersonEditorState>, { payload }: SetIsNonUSAddress) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          PersonalDetailsForm: patch({
            model: patch({
              IsNonUSAddress: payload
            })
          })
        })
      })
    );
  }

  //TAXES FORM ACTIONS

  @Action(SetTaxesFormState)
  setTaxFormState(ctx: StateContext<IPersonEditorState>, { payload }: SetTaxesFormState) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            status: payload
          })
        })
      })
    );
  }

  @Action(SetFederalFilingStatus)
  setFederalFilingStatus(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetFederalFilingStatus
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              FederalFilingStatus: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetFederalWithholdingAllowance)
  setFederalWithholdingAllowance(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetFederalWithholdingAllowance
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              FederalWithholdingAllowance: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetFederalAddlWithholdingType)
  setFederalAddlWithholdingType(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetFederalAddlWithholdingType
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              FederalAddlWithholdingType: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetFederalAddlWithholdingValue)
  setFederalAddlWithholdingValue(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetFederalAddlWithholdingValue
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              FederalAddlWithholdingValue: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetHasSSExemption)
  setHasSSExemption(ctx: StateContext<IPersonEditorState>, { payload }: SetHasSSExemption) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              HasSSExemption: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetHasMEDCExemption)
  setHasMEDCExemption(ctx: StateContext<IPersonEditorState>, { payload }: SetHasMEDCExemption) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              HasMEDCExemption: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetHasSUIExemption)
  setHasSUIExemption(ctx: StateContext<IPersonEditorState>, { payload }: SetHasSUIExemption) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              HasSUIExemption: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetHasFUTAExemption)
  setHasFUTAExemption(ctx: StateContext<IPersonEditorState>, { payload }: SetHasFUTAExemption) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              HasFUTAExemption: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetHasDisLeaveExempt)
  setHasDisLeaveExempt(ctx: StateContext<IPersonEditorState>, { payload }: SetHasDisLeaveExempt) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              HasDisLeaveExempt: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetHasFamilyLeaveExempt)
  setHasFamilyLeaveExempt(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetHasFamilyLeaveExempt
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              HasFamilyLeaveExempt: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetHasMedLeaveExempt)
  setHasMedLeaveExempt(ctx: StateContext<IPersonEditorState>, { payload }: SetHasMedLeaveExempt) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              HasMedLeaveExempt: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetFUTAExemptionReason)
  setFUTAExemptionReason(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetFUTAExemptionReason
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              FUTAExemptionReason: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetJ1F1Visa)
  setJ1F1Visa(ctx: StateContext<IPersonEditorState>, { payload }: SetJ1F1Visa) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              J1F1Visa: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetExpireDate)
  setExpireDate(ctx: StateContext<IPersonEditorState>, { payload }: SetExpireDate) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              ExpireDate: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetWithHoldingState)
  setWithHoldingState(ctx: StateContext<IPersonEditorState>, { payload }: SetWithHoldingState) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              WithHoldingState: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetStateFilingStatus)
  setStateFilingStatus(ctx: StateContext<IPersonEditorState>, { payload }: SetStateFilingStatus) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              StateFilingStatus: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetStateWithholdingAllowance)
  setStateWithholdingAllowance(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetStateWithholdingAllowance
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              StateWithholdingAllowance: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetStateAddlAllowances)
  setStateAddlAllowances(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetStateAddlAllowances
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              StateAddlAllowances: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetStateAddlWithholdingType)
  setStateAddlWithholdingType(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetStateAddlWithholdingType
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              StateAddlWithholdingType: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetStateAddlWithholdingValue)
  setStateAddlWithholdingValue(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetStateAddlWithholdingValue
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              StateAddlWithholdingValue: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetAddlStateFilingStatus)
  setAddlStateFilingStatus(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetAddlStateFilingStatus
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              AddlStateFilingStatus: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetAddlStateWithholdingAllowance)
  setAddlStateWithholdingAllowance(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetAddlStateWithholdingAllowance
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              AddlStateWithholdingAllowance: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetAddlStateAddlAllowances)
  setAddlStateAddlAllowances(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetAddlStateAddlAllowances
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              AddlStateAddlAllowances: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetAddlStateAddlWithholdingType)
  setAddlStateAddlWithholdingType(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetAddlStateAddlWithholdingType
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              AddlStateAddlWithholdingType: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetAddlStateAddlWithholdingValue)
  setAddlStateAddlWithholdingValue(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetAddlStateAddlWithholdingValue
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              AddlStateAddlWithholdingValue: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetIsExemptFromWithholding)
  setIsExemptFromWithholding(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetIsExemptFromWithholding
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              IsExemptFromWithholding: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetW4Version)
  setW4Version(ctx: StateContext<IPersonEditorState>, { payload }: SetW4Version) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              W4Version: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetIsHigherWithholding)
  setIsHigherWithholding(
    ctx: StateContext<IPersonEditorState>,
    { payload }: SetIsHigherWithholding
  ) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              IsHigherWithholding: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetChildTaxCredit)
  setChildTaxCredit(ctx: StateContext<IPersonEditorState>, { payload }: SetChildTaxCredit) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              ChildTaxCredit: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetOtherIncome)
  setOtherIncome(ctx: StateContext<IPersonEditorState>, { payload }: SetOtherIncome) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              OtherIncome: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetWageDeduction)
  setWageDeduction(ctx: StateContext<IPersonEditorState>, { payload }: SetWageDeduction) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              WageDeduction: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }

  @Action(SetLocals)
  setLocals(ctx: StateContext<IPersonEditorState>, { payload }: SetLocals) {
    ctx.setState(
      patch<IPersonEditorState>({
        Forms: patch({
          TaxesForm: patch({
            model: patch({
              Locals: payload
            })
          })
        }),
        IsCurrentTabDirty: true
      })
    );
  }
}

export class PersonalDetailsFormSelectors {
  @Selector([PersonEditorState])
  static personalDetailsFormModel(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model;
  }

  @Selector([PersonEditorState])
  static employmentTypeId(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.EmploymentTypeId;
  }

  @Selector([PersonEditorState])
  static useEIN(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.UseEIN;
  }

  @Selector([PersonEditorState])
  static federalIdentifier(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.FederalIdentifier;
  }

  @Selector([PersonEditorState])
  static verifyFederalIdentifier(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.VerifyFederalIdentifier;
  }

  @Selector([PersonEditorState])
  static isCompany(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.IsCompany;
  }

  @Selector([PersonEditorState])
  static firstName(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.FirstName;
  }

  @Selector([PersonEditorState])
  static middleName(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.MiddleName;
  }

  @Selector([PersonEditorState])
  static lastName(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.LastName;
  }

  @Selector([PersonEditorState])
  static birthday(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.Birthday;
  }

  @Selector([PersonEditorState])
  static personalEmailAddress(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.PersonalEmailAddress;
  }

  @Selector([PersonEditorState])
  static homePhoneNumber(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.HomePhoneNumber;
  }

  @Selector([PersonEditorState])
  static mobilePhoneNumber(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.MobilePhoneNumber;
  }

  @Selector([PersonEditorState])
  static gender(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.Gender;
  }

  @Selector([PersonEditorState])
  static maritalStatus(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.MaritalStatus;
  }

  @Selector([PersonEditorState])
  static race(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.Race;
  }

  @Selector([PersonEditorState])
  static streetAddress(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.StreetAddress;
  }

  @Selector([PersonEditorState])
  static streetAddress2(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.StreetAddress2;
  }

  @Selector([PersonEditorState])
  static city(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.City;
  }

  @Selector([PersonEditorState])
  static state(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.State;
  }

  @Selector([PersonEditorState])
  static postalCode(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.PostalCode;
  }

  @Selector([PersonEditorState])
  static country(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.Country;
  }

  @Selector([PersonEditorState])
  static isNonUSAddress(state: IPersonEditorState) {
    return state.Forms.PersonalDetailsForm.model.IsNonUSAddress;
  }
}

export class JobDetailsFormSelectors {
  @Selector([PersonEditorState])
  static employeeId(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.EmployeeId;
  }

  @Selector([PersonEditorState])
  static hireDate(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.HireDate;
  }

  @Selector([PersonEditorState])
  static shouldReportAsNewHire(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.ShouldReportAsNewHire;
  }

  @Selector([PersonEditorState])
  static worksIn(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.WorksIn;
  }

  @Selector([PersonEditorState])
  static okToAutoPay(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.OKToAutoPay;
  }

  @Selector([PersonEditorState])
  static payFrequency(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.PayFrequency;
  }

  @Selector([PersonEditorState])
  static location(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.Location;
  }

  @Selector([PersonEditorState])
  static division(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.Division;
  }

  @Selector([PersonEditorState])
  static department(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.Department;
  }

  @Selector([PersonEditorState])
  static jobTitle(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.JobTitle;
  }

  @Selector([PersonEditorState])
  static isSeasonal(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.IsSeasonal;
  }

  @Selector([PersonEditorState])
  static fullTimePartTime(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.FullTimePartTime;
  }

  @Selector([PersonEditorState])
  static isOwnerOfficer(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.IsOwnerOfficer;
  }

  @Selector([PersonEditorState])
  static socCode(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.SOCCode;
  }

  @Selector([PersonEditorState])
  static occupationId(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.OccupationId;
  }

  @Selector([PersonEditorState])
  static basisOfPayment(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.BasisOfPayment;
  }

  @Selector([PersonEditorState])
  static workEmail(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.WorkEmailAddress;
  }

  @Selector([PersonEditorState])
  static workPhoneNumber(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.WorkPhoneNumber;
  }

  @Selector([PersonEditorState])
  static originalHireDate(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.OriginalHireDate;
  }

  @Selector([PersonEditorState])
  static includeNonW2InWorkersComp(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.IncludeNonW2InWorkersComp;
  }

  @Selector([PersonEditorState])
  static workersCompCode(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.WorkersCompCode;
  }

  @Selector([PersonEditorState])
  static workersCompOwnerStatus(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.WorkersCompOwnerStatus;
  }

  @Selector([PersonEditorState])
  static workersCompLimitId(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.WorkersCompLimitId;
  }

  @Selector([PersonEditorState])
  static laborAllocationTotal(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model.LaborAllocationTotal;
  }

  @Selector([PersonEditorState])
  static jobDetailsFormModel(state: IPersonEditorState) {
    return state.Forms.JobDetailsForm.model;
  }
}

export class TaxesFormSelectors {
  @Selector([PersonEditorState])
  static federalFilingStatus(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.FederalFilingStatus;
  }

  @Selector([PersonEditorState])
  static federalWithholdingAllowance(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.FederalWithholdingAllowance;
  }

  @Selector([PersonEditorState])
  static federalAddlWithholdingType(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.FederalAddlWithholdingType;
  }

  @Selector([PersonEditorState])
  static federalAddlWithholdingValue(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.FederalAddlWithholdingValue;
  }

  @Selector([PersonEditorState])
  static hasSSExemption(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.HasSSExemption;
  }

  @Selector([PersonEditorState])
  static hasMEDCExcemption(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.HasMEDCExemption;
  }

  @Selector([PersonEditorState])
  static hasSUIExemption(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.HasSUIExemption;
  }

  @Selector([PersonEditorState])
  static hasFUTAExemption(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.HasFUTAExemption;
  }

  @Selector([PersonEditorState])
  static hasDisLeaveExempt(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.HasDisLeaveExempt;
  }

  @Selector([PersonEditorState])
  static hasFamilyLeaveExempt(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.HasFamilyLeaveExempt;
  }

  @Selector([PersonEditorState])
  static hasMedLeaveExempt(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.HasMedLeaveExempt;
  }

  @Selector([PersonEditorState])
  static FUTAExemptionReason(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.FUTAExemptionReason;
  }

  @Selector([PersonEditorState])
  static J1F1Visa(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.J1F1Visa;
  }

  @Selector([PersonEditorState])
  static expireDate(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.ExpireDate;
  }

  @Selector([PersonEditorState])
  static withholdingState(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.WithHoldingState;
  }

  @Selector([PersonEditorState])
  static stateFilingStatus(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.StateFilingStatus;
  }

  @Selector([PersonEditorState])
  static stateWithholdingAllowance(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.StateWithholdingAllowance;
  }

  @Selector([PersonEditorState])
  static stateAddlAllowances(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.StateAddlAllowances;
  }

  @Selector([PersonEditorState])
  static stateAddlWithholdingType(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.StateAddlWithholdingType;
  }

  @Selector([PersonEditorState])
  static stateAddlWithholdingValue(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.StateAddlWithholdingValue;
  }

  @Selector([PersonEditorState])
  static addlStateFilingStatus(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.AddlStateFilingStatus;
  }

  @Selector([PersonEditorState])
  static addlStateWithholdingAllowance(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.AddlStateWithholdingAllowance;
  }

  @Selector([PersonEditorState])
  static addlStateAddlAllowances(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.AddlStateAddlAllowances;
  }

  @Selector([PersonEditorState])
  static addlStateAddlWithholdingType(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.AddlStateAddlWithholdingType;
  }

  @Selector([PersonEditorState])
  static addlStateAddlWithholdingValue(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.AddlStateAddlWithholdingValue;
  }

  @Selector([PersonEditorState])
  static isExemptFromWithholding(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.IsExemptFromWithholding;
  }

  @Selector([PersonEditorState])
  static W4Version(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.W4Version;
  }

  @Selector([PersonEditorState])
  static isHigherWithholding(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.IsHigherWithholding;
  }

  @Selector([PersonEditorState])
  static childTaxCredit(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.ChildTaxCredit;
  }

  @Selector([PersonEditorState])
  static otherIncome(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.OtherIncome;
  }

  @Selector([PersonEditorState])
  static wageDeduction(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.WageDeduction;
  }

  @Selector([PersonEditorState])
  static locals(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model.Locals;
  }

  @Selector([PersonEditorState])
  static taxesFormModel(state: IPersonEditorState) {
    return state.Forms.TaxesForm.model;
  }
}
